<template>
  <div class="reminder-ai-tab">
    <!-- Header riêng thay cho title mặc định của b-modal — có thêm dòng phụ đề.
         Nút đóng emit ra ngoài, UserPackageUpdate.vue lắng nghe để tắt modal
         (b-modal cha đã bật hide-header). -->
    <div class="ai-topbar">
      <div class="ai-topbar__text">
        <h1>Duyệt nhắc nhở bằng AI</h1>
        <p>Tạo và duyệt nội dung nhắc nhở cho các hoạt động sắp tới</p>
      </div>
      <button type="button" class="ai-topbar__close" aria-label="Đóng" @click="$emit('close')">
        <i class="ki ki-close" />
      </button>
    </div>

    <!-- Thanh bước dạng chấm-nối-đường (không dùng wizard-4 của theme nữa) —
         chỉ là UI thuần CSS, logic điều hướng vẫn y nguyên qua goToStep/maxStep. -->
    <div class="ai-stepper mb-6">
      <div
        v-for="s in STEPS"
        :key="s.n"
        class="ai-stepper__item"
        :class="{
          'is-current': currentStep === s.n,
          'is-done': s.n < currentStep,
          'is-locked': s.n > maxStep,
        }"
        :title="s.n > maxStep ? lockedReason(s.n) : ''"
        @click="goToStep(s.n)"
      >
        <span v-if="s.n > 1 && s.n < currentStep" class="ai-stepper__connector ai-stepper__connector--done" />
        <span v-else-if="s.n > 1" class="ai-stepper__connector" />
        <span class="ai-stepper__dot">
          <i v-if="s.n < currentStep" class="flaticon2-check-mark" />
          <template v-else>{{ s.n }}</template>
        </span>
        <span class="ai-stepper__label">{{ s.label }}</span>
      </div>
    </div>

    <!-- Ngữ cảnh: suy ra hết từ khách hàng, chỉ hiển thị chứ không cho chọn -->
    <div v-if="currentStep === 1" class="panel">
      <div class="panel-head">
        <span class="step-tag">Bước 1</span>
        <h2>Chuẩn hoá Prompt</h2>
      </div>
      <p class="desc">
        Chốt nội dung khách hàng sẽ đưa vào prompt trước khi gọi AI — nhóm xưng hô,
        thông tin lấy từ đâu, chỉ số hay câu khảo sát nào được dùng.
      </p>

      <div class="panel-body">
        <b-overlay :show="loadingContext">
          <b-alert :show="!!context.blocker" variant="warning">
            {{ context.blocker }}
          </b-alert>

          <b-alert :show="isPlanOnly" variant="secondary">
            Gói đang là <b>nháp</b> — chưa hiện trong danh sách, bỏ ngang quá 48h sẽ tự mất.
          </b-alert>

          <div class="ctx-head mb-4">
            <span class="ctx-name">{{ context.customerName || '—' }}</span>
          </div>

          <!-- Ghi chú coach cho AI chat Zalo: điều coach biết mà dữ liệu không có (thực đơn bác sĩ đã duyệt,
               khách dễ khó tiêu...). Lưu vào hồ sơ đối tượng, AI đọc mỗi lượt soạn tin. Độc lập với nhắc nhở. -->
          <div class="mb-4">
            <label class="d-block mb-2" for="coach-note">Ghi chú cho AI chat (coach biết, dữ liệu chưa có)</label>
            <b-form-textarea
              id="coach-note"
              v-model="coachNote"
              rows="3"
              placeholder="Ví dụ: Đang theo thực đơn BS dinh dưỡng duyệt ngày 03/11. Ăn đậu dễ khó tiêu. Hẹn tư vấn chiều thứ 7 lúc 10h30."
            />
            <div class="d-flex align-items-center mt-2">
              <b-button size="sm" variant="light" :disabled="coachNoteSaving" @click="saveCoachNote">Lưu ghi chú</b-button>
              <span v-if="coachNoteSavedAt" class="text-muted font-size-sm ml-3">Đã lưu lúc {{ coachNoteSavedAt }}. AI dùng từ tin tiếp theo.</span>
            </div>
          </div>

          <!-- Hai lựa chọn quyết định prompt, để cạnh nhau cho dễ đối chiếu -->
          <b-row>
            <b-col md="6">
              <basic-select
                label="Nhóm xưng hô"
                placeholder="--- Chọn nhóm ---"
                name="titleOverride"
                :options="titleOptions"
                track-by="value"
                value-label="text"
                :value.sync="titleOverride"
              />
              <p class="text-muted font-size-sm mt-n3">
                <template v-if="isTitleOverridden">
                  Khác hồ sơ khách ({{ titleName(context.customerTitle) }}).
                  Prompt nhóm cũng đổi theo.
                </template>
                <template v-else>Lấy theo hồ sơ khách hàng.</template>
              </p>
            </b-col>

            <b-col md="6">
              <basic-select
                label="Thông tin khách hàng đưa vào prompt"
                placeholder="Lấy từ hồ sơ trên app"
                name="userInfoSource"
                :options="userInfoSourceOptions"
                track-by="value"
                value-label="text"
                :multiple="true"
                :change-value-by-object="true"
                :value.sync="userInfoSource"
              />
              <!-- Không dùng mt-n3 như ô bên cạnh: multiselect cao thấp tuỳ số
                   chip đang chọn, margin âm cố định là chữ đè lên ô chọn. -->
              <p class="text-muted font-size-sm mt-1">
                Chọn được nhiều nguồn cùng lúc — vừa lấy từ app vừa tự gõ thêm.
              </p>
            </b-col>
          </b-row>

          <!-- Phần phụ thuộc ô "Thông tin khách hàng": chọn nguồn nào thì hiện phần đó,
               chọn cả hai thì hiện cả hai, gộp chung vào prompt. Mỗi nguồn đều có
               title riêng để biết khối bên dưới ứng với lựa chọn nào ở trên. -->
          <template v-if="includesManualInfo">
            <label class="d-block mb-2">Thông tin tự gõ đưa vào prompt</label>
            <b-form-textarea
              v-model="userInfoOverride"
              rows="4"
              class="mb-4"
              placeholder="Ví dụ: Tên: Nguyễn Văn A&#10;Năm sinh: 1958&#10;Tình trạng: tiểu đường type 2, 5 năm"
            />
          </template>

          <template v-if="includesAppInfo">
            <label class="d-block mb-2">Chỉ số đưa vào prompt</label>

            <div v-if="!availableMetrics.length" class="ctx-empty mb-4">
              Khách chưa nhập chỉ số nào trên app trong 90 ngày.
            </div>

            <!-- Tree: mỗi chỉ số 1 dòng gọn, mở rộng mới hiện đủ 4 mốc 7/14/30/90 ngày
                 xếp dọc — đỡ dồn hết vào 1 dòng dày như trước. Mỗi mốc có checkbox riêng:
                 tích mốc nào thì mốc đó vào prompt cho chỉ số này, không bắt buộc đủ cả 4. -->
            <div v-else class="metric-tree mb-4">
              <div
                v-for="m in availableMetrics"
                :key="m.key"
                class="metric-node"
              >
                <div class="metric-node__row">
                  <b-form-checkbox
                    v-model="metricKeys"
                    :value="m.key"
                    class="mb-0"
                  />
                  <button
                    type="button"
                    class="metric-node__toggle"
                    @click="toggleMetricExpand(m.key)"
                  >
                    <span class="metric-node__name">{{ m.name }}</span>
                    <span class="metric-node__badge text-muted">
                      <template v-if="m.latestValue">
                        gần nhất {{ m.latestValue }}
                        <template v-if="m.daysSinceLatest === 0">(hôm nay)</template>
                        <template v-else>({{ m.daysSinceLatest }} ngày trước)</template>
                      </template>
                      <template v-else>chưa nhập lần nào</template>
                    </span>
                    <i
                      class="metric-node__chevron"
                      :class="{ 'is-open': expandedMetric === m.key }"
                    >▾</i>
                  </button>
                </div>

                <div v-if="expandedMetric === m.key" class="metric-node__detail">
                  <!-- Tích mốc nào thì mốc đó vào prompt cho CHỈ SỐ NÀY — mỗi chỉ số tự
                       chọn mốc riêng, không bắt buộc đủ cả 4 như thiết kế cũ. -->
                  <div v-for="w in metricWindows(m)" :key="w.days" class="metric-window">
                    <div class="metric-window__row">
                      <b-form-checkbox
                        :checked="isWindowSelected(m.key, w.days)"
                        class="mb-0"
                        @change="toggleWindowSelected(m.key, w.days)"
                      />
                      <button
                        type="button"
                        class="metric-window__toggle"
                        @click="toggleWindowExpand(m.key, w.days)"
                      >
                        <span class="metric-window__label">{{ w.days }} ngày</span>
                        <span class="metric-window__value">
                          {{ w.count }} lần
                          <template v-if="w.average"> · TB {{ w.average }}</template>
                        </span>
                        <i
                          class="metric-window__chevron"
                          :class="{ 'is-open': expandedWindow === m.key + ':' + w.days }"
                        >▾</i>
                      </button>
                    </div>

                    <!-- Mở tiếp 1 mốc mới thấy từng lần đo cụ thể, lọc ngay trên dữ liệu
                         90 ngày đã có sẵn — không gọi thêm API cho mỗi mốc. -->
                    <div
                      v-if="expandedWindow === m.key + ':' + w.days"
                      class="metric-window__entries"
                    >
                      <div v-if="!entriesForWindow(m.entries, w.days).length" class="text-muted">
                        Chưa có lần đo nào trong khoảng này.
                      </div>
                      <div
                        v-for="(e, idx) in entriesForWindow(m.entries, w.days)"
                        :key="idx"
                        class="metric-window__entry"
                      >
                        <span>{{ e.value }}</span>
                        <span class="text-muted">{{ formatEntryDateTime(e.at) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-if="includesSurveyInfo">
            <label class="d-block mb-2">Câu trả lời khảo sát đưa vào prompt</label>

            <div v-if="!surveyGroups.length" class="ctx-empty mb-4">
              Khách chưa trả lời khảo sát nào.
            </div>

            <div v-else class="mb-4">
              <b-button variant="outline-info" size="sm" @click="showSurvey = true">
                Đang chọn {{ surveyQuestionIds.length }}/{{ totalSurveyQuestions }} câu
                · {{ selectedSurveyGroupIds.length }}/{{ surveyGroups.length }} bộ khảo sát
              </b-button>

              <p v-if="surveyInsight" class="text-success font-size-sm mt-2 mb-0">
                Đang dùng bản nhận định ({{ surveyInsight.length }} ký tự) thay cho danh sách hỏi-đáp.
              </p>
            </div>
          </template>

          <hr class="divider">

          <div class="footer-row justify-content-end">
            <span v-if="step1BlockReason" class="text-danger font-size-sm mr-3">
              {{ step1BlockReason }}
            </span>
            <b-button
              variant="success"
              :disabled="!step1Complete"
              @click="goToStep(2)"
            >
              Tiếp theo: Gọi AI →
            </b-button>
          </div>
        </b-overlay>
      </div>
    </div>

    <!-- Gọi AI -->
    <div v-if="currentStep === 2" class="panel">
      <div class="panel-head">
        <span class="step-tag">Bước 2</span>
        <h2>Tạo gợi ý cho {{ context.scheduledCount || 0 }} hoạt động</h2>
      </div>
      <p class="desc desc--hero">
        AI sẽ đọc thông tin của <strong>{{ context.scheduledCount || 0 }} hoạt động</strong>
        dưới đây và đề xuất nội dung nhắc nhở phù hợp cho từng hoạt động. Bạn có thể xem
        trước dữ liệu và prompt trước khi tạo.
      </p>

      <div class="panel-body">
        <b-overlay :show="loadingContext">
          <!-- Quyết định cơ chế gửi cho cả gói: duyệt từng tin (hỏi lại qua Zalo lúc
               gửi) hay duyệt trước nội dung (portal duyệt là chốt, gửi thẳng). Lưu
               ngay khi đổi, không cần nút Lưu riêng. -->
          <b-row class="mb-4 justify-content-center">
            <b-col md="6">
              <b-form-group label="Cách duyệt nhắc nhở" class="mb-2">
                <b-form-radio-group
                  v-model="approvalMode"
                  :options="approvalModeOptions"
                  :disabled="savingApprovalMode"
                  stacked
                  @change="handleApprovalModeChange"
                />
              </b-form-group>
              <p class="text-muted font-size-sm mb-0">
                <template v-if="approvalModeValue === 1">
                  Duyệt ở bước 3 là chốt — tới giờ gửi thẳng, không hỏi lại qua Zalo.
                </template>
                <template v-else>
                  Duyệt ở bước 3 chỉ để đặt lịch — tới giờ gửi vẫn nhắn lại coach qua
                  Zalo xác nhận lần cuối trước khi gửi.
                </template>
              </p>
            </b-col>
          </b-row>

          <!-- Tiến độ thật từ BE: AI chạy theo lô, done tăng sau mỗi lô -->
          <div v-if="generating && generateProgress && generateProgress.total" class="approve-progress mb-4">
            <div class="approve-progress__label">
              <template v-if="generateProgress.stage === 'build'">
                Đang dựng hoạt động {{ generateProgress.done }}/{{ generateProgress.total }}
              </template>
              <template v-else>
                Đang tạo gợi ý {{ generateProgress.done }}/{{ generateProgress.total }} nhắc nhở
              </template>
            </div>
            <b-progress
              :value="generateProgress.done"
              :max="generateProgress.total"
              variant="success"
              height="6px"
              animated
            />
          </div>
          <!-- BE chưa báo total: còn đang dựng hoạt động / nạp nội dung, lần đầu mất lâu nhất -->
          <div v-else-if="generating" class="approve-progress mb-4">
            <div class="approve-progress__label">
              Đang chuẩn bị dữ liệu (dựng hoạt động, nạp nội dung nhắc nhở)...
            </div>
            <b-progress :value="100" :max="100" variant="info" height="6px" striped animated />
          </div>

          <b-alert
            :show="!!generateMessage"
            :variant="generateFailed ? 'warning' : 'info'"
            class="mb-4"
          >
            {{ generateMessage }}
          </b-alert>

          <hr class="divider">

          <div class="footer-row">
            <button type="button" class="back-btn" @click="goToStep(1)">← Quay lại</button>

            <!-- Dồn hết thao tác về một chỗ: việc xem trước, rồi hành động chính.
                 Hành động chính là một nút duy nhất — chưa có gợi ý thì nó gọi AI, có rồi
                 (kể cả nháp đọc lại từ Redis) thì đổi thành nút sang bước duyệt. Hai nút
                 riêng khiến người dùng phải tự đoán cái nào đang dùng được. -->
            <div class="footer-action">
              <button
                type="button"
                class="btn btn--data"
                :disabled="!userPackageId"
                @click="openPlanned"
              >
                Xem {{ context.scheduledCount || 0 }} hoạt động
              </button>

              <!-- Nút "Xem prompt gửi AI" đã ẩn. handlePreviewPrompt và modal xem prompt
                   vẫn giữ lại để bật lên nhanh khi cần soi prompt lúc debug. -->

              <button
                v-if="!step2Complete"
                type="button"
                class="btn-view btn-view--primary"
                :disabled="generating || !userPackageId || !!context.blocker || !plannedConfirmed"
                @click="handleGenerate"
              >
                <b-spinner v-if="generating" small />
                {{
                  generating
                    ? 'Đang tạo gợi ý...'
                    : `Tạo gợi ý cho ${context.scheduledCount || 0} nhắc nhở`
                }}
              </button>

              <button
                v-else
                type="button"
                class="btn-view"
                @click="goToStep(3)"
              >
                Xem gợi ý <span class="count">{{ items.length }}</span> →
              </button>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>

    <!-- Danh sách chờ duyệt -->
    <div v-if="currentStep === 3" class="panel">
      <div class="panel-head">
        <span class="step-tag">Bước 3</span>
        <h2>Duyệt gợi ý</h2>
        <span v-if="items.length" class="text-muted font-size-sm ml-2">
          {{ pendingCount }} chờ duyệt / {{ items.length }} tổng
        </span>
      </div>

      <div class="panel-body">
        <!-- Chỉ còn nút tải lại ở đây; hai thao tác quyết định nằm ở hàng cuối trang -->
        <div class="d-flex justify-content-end align-items-center mb-4">
          <b-button variant="light" size="sm" :disabled="loading" @click="loadData">
            Tải lại
          </b-button>
        </div>

        <!-- Gói AI Mode fail-closed: chưa duyệt là tới giờ huỷ, khách không nhận gì -->
        <b-alert :show="pastDueCount > 0" variant="danger">
          Có <b>{{ pastDueCount }}</b> nhắc nhở giờ gửi đã qua. Duyệt là đặt lịch, nên phải
          <b>đổi giờ trước</b> — bấm "Đổi giờ gửi" ở dòng tô đỏ.
        </b-alert>

        <b-alert :show="pendingCount > 0" variant="warning">
          Còn <b>{{ pendingCount }}</b> nhắc nhở chờ duyệt. Nhắc nhở nào tới giờ gửi mà
          coach chưa duyệt sẽ bị huỷ, khách hàng không nhận được gì.
        </b-alert>

        <b-alert :show="!items.length" variant="warning">
          Gói này đang ở chế độ <b>AI Mode</b> nhưng chưa có gợi ý nào.
          Chưa bấm <b>Gợi ý từ AI</b> thì mọi nhắc nhở của gói đều bị huỷ khi tới giờ.
        </b-alert>

        <b-overlay :show="loading">
          <div v-if="!items.length" class="text-center text-muted py-10">
            Chưa có gợi ý nào. Bấm <b>Gợi ý từ AI</b> ở trên để bắt đầu.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-vertical-center">
              <thead>
                <tr>
                  <th style="width: 165px">Mã / Giờ gửi</th>
                  <th style="width: 120px">Kênh</th>
                  <th>Nội dung gốc</th>
                  <th>Gợi ý từ AI</th>
                  <th v-if="showPerItemReview" style="width: 110px">Trạng thái</th>
                  <th :style="showPerItemReview ? 'width: 200px' : 'width: 110px'">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in items"
                  :key="item.id"
                  :class="{ 'table-danger': isPastDue(item) }"
                >
                  <td>
                    <div class="font-weight-bold">{{ item.code }}</div>
                    <div
                      class="font-size-sm send-at"
                      :class="isPastDue(item) ? 'text-danger font-weight-bold' : 'text-muted'"
                    >
                      {{ formatDateTime(item.scheduledSendAt) }}
                      <span v-if="isPastDue(item)" class="d-block">· đã qua</span>
                    </div>
                    <div v-if="item.notifiedAt" class="text-muted font-size-sm">
                      Đã hỏi coach: {{ formatDateTime(item.notifiedAt) }}
                    </div>
                  </td>

                  <td>
                    <span
                      class="label label-inline"
                      :class="isZaloItem(item) ? 'label-light-primary' : 'label-light-info'"
                    >
                      {{ isZaloItem(item) ? 'Zalo' : 'App' }}
                    </span>
                    <div class="text-muted font-size-sm mt-1">
                      {{ channelNote(item) }}
                    </div>
                  </td>

                  <td
                    class="text-muted source-cell"
                    v-html="renderContent(item.sourceContent) || '—'"
                  ></td>

                  <td>
                    <template v-if="editingContentSet.has(item.id)">
                      <b-form-textarea
                        v-model="contentEditsById[item.id]"
                        rows="4"
                        size="sm"
                      />
                      <div class="mt-2">
                        <b-button
                          variant="success"
                          size="sm"
                          class="mr-2"
                          :disabled="busy || !contentEditsById[item.id] || !contentEditsById[item.id].trim()"
                          @click="handleSaveContentEdit(item)"
                        >
                          Lưu nội dung
                        </b-button>
                        <b-button
                          variant="outline-secondary"
                          size="sm"
                          :disabled="busy"
                          @click="toggleContentEdit(item)"
                        >
                          Huỷ
                        </b-button>
                      </div>
                    </template>

                    <template v-else>
                      <div class="content-cell" v-html="renderContent(item.finalContent)"></div>

                      <div v-if="item.editPercent > 0" class="text-muted font-size-sm mt-1">
                        Coach đã sửa {{ item.editPercent }}%
                      </div>
                    </template>
                  </td>

                  <td v-if="showPerItemReview">
                    <span
                      class="label label-inline label-lg"
                      :class="`label-light-${statusVariant(item.status)}`"
                    >
                      {{ statusText(item.status) }}
                    </span>
                  </td>

                  <td>
                    <!-- Mọi thao tác của một dòng gom về đây: trước đó icon rải ở cột
                         Mã / Giờ gửi và cột Gợi ý từ AI, phải đi tìm mới thấy. -->
                    <div class="row-actions">
                      <a
                        v-if="canReview(item)"
                        href="#"
                        title="Sửa nội dung"
                        @click.prevent="toggleContentEdit(item)"
                      >
                        <i class="flaticon2-edit" />
                      </a>
                      <a
                        v-if="canChangeTime(item)"
                        href="#"
                        title="Đổi giờ gửi"
                        @click.prevent="openChangeTime(item)"
                      >
                        <i class="flaticon2-calendar-4" />
                      </a>
                      <a
                        v-if="canDeleteItem(item)"
                        href="#"
                        class="row-actions__danger"
                        title="Xoá nhắc nhở này"
                        @click.prevent="handleDeleteItem(item)"
                      >
                        <i class="flaticon2-trash" />
                      </a>
                    </div>

                    <template v-if="showPerItemReview && canReview(item)">
                      <b-button
                        variant="success"
                        size="sm"
                        class="mr-2 mb-2"
                        :disabled="busy || isPastDue(item)"
                        :title="isPastDue(item) ? 'Đổi giờ gửi trước đã' : ''"
                        @click="handleReview(item, REVIEW_ACTION.OK)"
                      >
                        Duyệt
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="mb-2"
                        :disabled="busy"
                        @click="handleReview(item, REVIEW_ACTION.SKIP)"
                      >
                        Bỏ qua
                      </b-button>
                    </template>

                    <!-- Chỉ khi thật sự không còn gì làm được (nhắc nhở đã gửi) — không
                         phải "else" của nút Duyệt, vì mode duyệt từng tin ẩn nút đó mà ba
                         icon phía trên vẫn dùng được. -->
                    <div
                      v-if="!canReview(item) && !canChangeTime(item) && !canDeleteItem(item)"
                      class="text-muted font-size-sm"
                    >
                      Không còn thao tác được
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-overlay>

        <!-- Tiến độ thật, không phải thanh chạy giả: vòng lặp duyệt gửi đi từng nhắc nhở
             một, nên done/total đúng bằng số request đã xong. -->
        <div v-if="approveProgress" class="approve-progress">
          <div class="approve-progress__label">
            {{ showPerItemReview ? 'Đang duyệt' : 'Đang tạo nhắc nhở' }}
            {{ approveProgress.done }}/{{ approveProgress.total }}
          </div>
          <b-progress
            :value="approveProgress.done"
            :max="approveProgress.total"
            variant="success"
            height="6px"
            animated
          />
        </div>

        <hr class="divider">

        <div class="footer-row">
          <button type="button" class="back-btn" @click="goToStep(2)">← Quay lại bước Gọi AI</button>

          <div class="footer-action">
            <b-button
              v-if="pendingCount > 0"
              variant="outline-success"
              size="sm"
              :disabled="busy"
              @click="handleApproveAll"
            >
              {{ showPerItemReview ? 'Duyệt tất cả' : 'Tạo nhắc nhở' }}
              ({{ pendingCount }})
            </b-button>
            <b-button
              v-if="pendingCount > 0"
              variant="outline-danger"
              size="sm"
              :disabled="busy"
              @click="handleDiscardDraft"
            >
              Xoá nháp, tạo lại
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Đổi giờ gửi -->
    <b-modal
      v-model="showPrompt"
      title="Prompt sẽ gửi cho AI"
      size="lg"
      scrollable
      hide-footer
    >
      <b-overlay :show="loadingPrompt">
        <p class="text-muted">{{ promptMessage }}</p>
        <pre class="prompt-preview">{{ promptPreview }}</pre>
      </b-overlay>
    </b-modal>

    <b-modal
      v-model="showSurvey"
      title="Câu trả lời khảo sát của khách"
      size="lg"
      scrollable
      ok-only
      ok-title="Xong"
    >
      <p class="text-muted">
        Tích chọn bộ khảo sát muốn dùng trước — bộ nào chưa tích thì không hiện câu
        và cũng không vào prompt. Trong bộ đã chọn, bỏ tích những câu không liên quan
        tới lời nhắc — câu hành chính, câu đặt lịch… Để càng nhiều thì prompt càng
        dài mà AI càng loãng.
      </p>

      <!-- Phân tích là một lượt gọi AI riêng: coach đọc được nhận định và sửa trước
           khi nó đi vào prompt sinh nhắc nhở. -->
      <div class="survey-analysis mb-4">
        <label class="d-block mb-2">Yêu cầu phân tích (tự gõ)</label>
        <b-form-textarea
          v-model="surveyAnalysisPrompt"
          rows="3"
          placeholder="Ví dụ: Đọc các câu trả lời trên, rút ra 3 điểm đáng chú ý nhất về thói quen ăn uống và vận động của khách, nêu rõ điểm nào cần nhắc thường xuyên."
        />

        <div class="d-flex align-items-center mt-2">
          <b-button
            variant="info"
            size="sm"
            :disabled="analyzingSurvey || !surveyAnalysisPrompt.trim() || !surveyQuestionIds.length"
            @click="handleAnalyzeSurvey"
          >
            <b-spinner v-if="analyzingSurvey" small class="mr-2" />
            {{ analyzingSurvey ? 'Đang phân tích...' : `Phân tích ${surveyQuestionIds.length} câu đã chọn` }}
          </b-button>

          <b-button
            v-if="surveyInsight"
            variant="link"
            size="sm"
            class="ml-2"
            :disabled="analyzingSurvey"
            @click="surveyInsight = ''"
          >
            Xoá nhận định
          </b-button>
        </div>

        <b-alert :show="!!surveyAnalysisMessage" variant="warning" class="mt-2 mb-0">
          {{ surveyAnalysisMessage }}
        </b-alert>

        <template v-if="surveyInsight">
          <label class="d-block mt-3 mb-2">
            Nhận định — sửa được, đoạn này sẽ vào prompt thay cho danh sách hỏi-đáp
          </label>
          <b-form-textarea v-model="surveyInsight" rows="6" />
        </template>
      </div>

      <div v-for="g in surveyGroups" :key="g.surveyId" class="survey-group">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <b-form-checkbox
            :checked="isSurveyGroupActive(g)"
            @change="toggleSurveyGroupActive(g)"
          >
            <b>{{ g.name }}</b>
            <small class="text-muted ml-2">
              <template v-if="g.daysSinceAnswered === 0">trả lời hôm nay</template>
              <template v-else-if="g.daysSinceAnswered !== null">
                trả lời {{ g.daysSinceAnswered }} ngày trước
              </template>
              · {{ g.sections.length }} phần · {{ g.answeredCount }} câu
            </small>
          </b-form-checkbox>
        </div>

        <!-- Chọn bộ ở trên rồi mới tới chọn câu trong bộ — bộ chưa tích thì
             không thấy phần/câu, mà cũng không đưa vào prompt. -->
        <template v-if="isSurveyGroupActive(g)">
          <div class="d-flex justify-content-end mb-1">
            <b-button variant="link" size="sm" @click="toggleSurveyGroup(g)">
              {{ isGroupFullySelected(g) ? 'Bỏ chọn cả bộ' : 'Chọn cả bộ' }}
            </b-button>
          </div>

          <!-- Một bộ có nhiều phần, hiếm khi cần lấy hết — phần hành chính, phần đặt
               lịch thường không liên quan gì tới lời nhắc. Tách ra để bỏ theo từng phần. -->
          <div v-for="sec in g.sections" :key="sec.sectionId" class="survey-section">
            <div class="d-flex align-items-center justify-content-between">
              <span class="survey-section__name">
                {{ sec.name }}
                <small class="text-muted ml-1">· {{ sec.lines.length }} câu</small>
              </span>

              <b-button variant="link" size="sm" @click="toggleSurveySection(sec)">
                {{ isSectionFullySelected(sec) ? 'Bỏ chọn phần' : 'Chọn phần' }}
              </b-button>
            </div>

            <b-form-checkbox-group v-model="surveyQuestionIds" stacked>
              <b-form-checkbox
                v-for="line in sec.lines"
                :key="line.questionId"
                :value="line.questionId"
                class="mb-2"
              >
                {{ line.question }}
                <span class="survey-answer">→ {{ line.answer }}</span>
              </b-form-checkbox>
            </b-form-checkbox-group>
          </div>
        </template>
      </div>
    </b-modal>

    <!-- Mở tự động khi vào Bước 2 và khoá nút Tạo gợi ý cho tới khi coach xem xong:
         gọi AI cho 84 nhắc nhở đặt sai giờ là tốn tiền thật và phải sinh lại từ đầu. -->
    <b-modal
      v-model="showPlanned"
      title="Các hoạt động sẽ được nhắc"
      size="xl"
      scrollable
      @hide="onPlannedHide"
      @hidden="confirmPlanned"
    >
      <template #modal-footer>
        <div class="planned-footer">
          <span class="text-muted font-size-sm">
            <template v-if="dirtyPlannedIds.length">
              Còn <b>{{ dirtyPlannedIds.length }}</b> thay đổi giờ gửi chưa lưu.
            </template>
            <template v-else-if="pastItems.length || outsideItems.length">
              Còn {{ pastItems.length + outsideItems.length }} nhắc nhở cần xử lý —
              vẫn xác nhận được, nhưng những dòng đó sẽ không gửi được.
            </template>
            <template v-else>
              Đã xem xong danh sách thì xác nhận để mở nút tạo gợi ý.
            </template>
          </span>

          <div class="planned-footer-actions">
            <b-button
              v-if="dirtyPlannedIds.length"
              variant="outline-secondary"
              :disabled="busy"
              @click="discardPlannedEdits"
            >
              Huỷ thay đổi
            </b-button>
            <b-button variant="success" :disabled="busy" @click="confirmAndClose">
              {{ dirtyPlannedIds.length ? 'Lưu & xác nhận' : 'Xác nhận' }}
            </b-button>
          </div>
        </div>
      </template>

      <b-overlay :show="loadingPlanned">
        <!-- Khung giờ sinh hoạt của khách.
             Đặt ngay trong màn này chứ không tách ra chỗ khác: mọi thứ cần để xử lý
             (sửa giờ từng dòng, chọn nhiều dòng để xoá, lưu hàng loạt) đều đã có sẵn
             ở bảng bên dưới — khối này chỉ tìm ra dòng lệch khung rồi đổ vào đúng
             những ô sửa đó. Coach vẫn bấm "Lưu thay đổi" như thường. -->
        <div class="period-box mb-4">
          <div class="period-bar">
            <div>
              <b class="font-size-lg">Khung giờ sinh hoạt của khách</b>
              <div class="period-sub">
                Đặt khung này trước. Mọi cách xử lý bên dưới — kể cả gợi ý của AI —
                đều chỉ được xếp giờ trong khung đã đặt.
              </div>
            </div>
            <!-- Chưa chốt khung thì không cho thu gọn: thu lại là mất luôn thanh xác
                 nhận, mà phần dưới vẫn khoá và không còn chỗ nào mở ra được. -->
            <b-button
              v-if="periodConfirmed"
              variant="link"
              size="sm"
              class="p-0"
              @click="showPeriodBox = !showPeriodBox"
            >
              {{ showPeriodBox ? 'Thu gọn' : 'Mở rộng' }}
            </b-button>
          </div>

          <template v-if="showPeriodBox">
            <p class="text-muted font-size-sm mb-3">
              Đặt khoảng giờ khách tiện nhận tin. Cần chi tiết hơn thì chia nhỏ theo buổi.
            </p>

            <!-- Khoảng duy nhất là mặc định: phần lớn khách chỉ có một câu "đừng nhắn
                 ngoài giờ này". Bốn buổi là trường hợp ít gặp hơn nên giấu sau một tick,
                 thay vì bắt ai cũng phải nhìn bốn thẻ ngay từ đầu.
                 Dùng select 24h chứ không dùng input[type=time]: input đó hiển thị
                 AM/PM theo locale của máy, lệch hẳn với phần còn lại của portal. -->
            <!-- Chia đôi: trái là một khoảng giờ, phải là 4 buổi. Cách nào đang không dùng thì mờ đi -->
            <b-row class="period-split">
              <b-col md="5">
                <div class="range-row" :class="{ off: splitByPeriod }">
                  <span>Nhận tin từ</span>
                  <b-form-select
                    v-model="rangeFrom"
                    :options="clockOptions"
                    size="sm"
                    class="tsel"
                    :disabled="splitByPeriod"
                    @change="savePeriods"
                  />
                  <span>đến</span>
                  <b-form-select
                    v-model="rangeTo"
                    :options="clockOptions"
                    size="sm"
                    class="tsel"
                    :disabled="splitByPeriod"
                    @change="savePeriods"
                  />
                </div>
                <div v-if="!splitByPeriod && rangeReversed" class="period-bad mt-1">
                  Giờ kết thúc phải sau giờ bắt đầu.
                </div>

                <b-form-checkbox v-model="splitByPeriod" class="mt-3" @change="savePeriods">
                  Chia nhỏ theo buổi (sáng / trưa / chiều / tối)
                </b-form-checkbox>
              </b-col>

              <!-- Mỗi buổi một thẻ nhỏ, xếp 2x2 -->
              <b-col md="7">
                <div class="period-grid" :class="{ off: !splitByPeriod }">
                  <div
                    v-for="p in periods"
                    :key="p.key"
                    class="period-card"
                    :class="{ on: splitByPeriod && p.enabled, bad: splitByPeriod && isReversedPeriod(p) }"
                  >
                    <b-form-checkbox v-model="p.enabled" :disabled="!splitByPeriod" @change="savePeriods">
                      {{ p.text }}
                    </b-form-checkbox>

                    <div class="period-time">
                      <b-form-select
                        v-model="p.from"
                        :options="clockOptions"
                        size="sm"
                        class="tsel"
                        :disabled="!splitByPeriod || !p.enabled"
                        @change="savePeriods"
                      />
                      <span class="tdash">–</span>
                      <b-form-select
                        v-model="p.to"
                        :options="clockOptions"
                        size="sm"
                        class="tsel"
                        :disabled="!splitByPeriod || !p.enabled"
                        @change="savePeriods"
                      />
                    </div>

                    <div v-if="splitByPeriod && isReversedPeriod(p)" class="period-bad">
                      Giờ kết thúc phải sau giờ bắt đầu, buổi này đang bị bỏ qua.
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Chốt khung giờ. Mốc mặc định 06:00–21:00 trông y hệt mốc coach tự chọn,
                 nên không có bước này thì không cách nào phân biệt "đã cân nhắc" với
                 "chưa ai nhìn tới" — mà mọi thứ bên dưới đều dựa vào nó. -->
            <!-- Mốc hỏi coach. Chỉ có nghĩa với gói "duyệt từng tin": gói "duyệt trước
                 nội dung" không đi qua vòng hỏi lại nào, bày ra chỉ làm coach tưởng có
                 thêm một cửa duyệt. -->
            <div v-if="!isPreApproved" class="confirm-row">
              <div class="confirm-line">
                <span>Nhắc coach trước</span>
                <b-form-input
                  v-model.number="confirmLeadMinutes"
                  type="number"
                  min="1"
                  max="1440"
                  class="nsel"
                  @change="savePeriods"
                />
                <span>phút, hết hiệu lực sau giờ gửi</span>
                <b-form-input
                  v-model.number="confirmTimeoutMinutes"
                  type="number"
                  min="1"
                  max="1440"
                  class="nsel"
                  @change="savePeriods"
                />
                <span>phút</span>
              </div>
              <div class="confirm-hint">
                Coach không trả lời trong khoảng đó thì nhắc nhở tự huỷ, khách không nhận gì.
              </div>
            </div>

            <div v-if="!periodConfirmed" class="period-confirm">
              <div class="period-confirm-text">
                Đang dùng khung mặc định <b>{{ scopeText || 'chưa hợp lệ' }}</b>.
                Sửa lại cho khớp nhịp sinh hoạt của khách, rồi xác nhận để mở phần xử lý bên dưới.
              </div>
              <b-button
                variant="success"
                size="sm"
                :disabled="!enabledPeriods.length || savingPeriods"
                @click="confirmPeriods"
              >
                Dùng khung giờ này
              </b-button>
            </div>

            <div v-else class="period-done">
              Đang áp khung <b>{{ scopeText }}</b>
              <b-button variant="link" size="sm" class="p-0 ml-2" @click="reopenPeriods">
                Đổi khung
              </b-button>
            </div>

            <template v-if="enabledPeriods.length && periodConfirmed">
              <div v-if="!outsideItems.length" class="period-ok">
                <!-- Phải nói rõ "còn lại": dòng đã quá giờ bị loại khỏi outsideItems, nên
                     một nhắc nhở 09:00 hôm qua vừa nằm trong hộp đỏ ở trên vừa không bị
                     tính là lệch khung. Đọc trơn hai câu cạnh nhau là thấy mâu thuẫn. -->
                <template v-if="pastItems.length">
                  Các nhắc nhở còn lại đều nằm trong khung giờ đã chọn.
                </template>
                <template v-else>Không có nhắc nhở nào lệch khung giờ.</template>
              </div>

              <template v-else>
                <div class="period-warn">
                  <b>{{ outsideItems.length }}</b> nhắc nhở nằm ngoài khung giờ đã chọn
                </div>

                <p class="period-warn-note">
                  Xử lý chung với nhóm bên dưới — mọi cách đặt lại giờ đều nằm ở đó.
                </p>
              </template>
            </template>
          </template>

          <!-- Quá giờ gửi là vấn đề KHÁC hẳn chuyện lệch khung, nên tách riêng và
               không giấu sau nút Thu gọn. Để lọt xuống Bước 3 thì Review ném 400
               "Giờ gửi ... đã qua rồi" từng dòng một, sau khi đã tốn cả lượt gọi AI.

               Đặt SAU phần cài khung giờ: khung giờ là thứ ràng buộc mọi cách xử lý
               bên dưới, kể cả prompt gửi cho AI. Bày nút "Nhờ AI xếp lại giờ" lên
               trước ô đặt khung là mời coach bấm khi chưa có gì làm giới hạn. -->
          <div v-if="needFixItems.length" class="past-box">
            <div class="past-head">
              <b>{{ needFixLabel }}</b>
              <b-button variant="link" size="sm" class="p-0" @click="showPastList = !showPastList">
                {{ showPastList ? 'Ẩn danh sách' : 'Xem danh sách' }}
              </b-button>
            </div>
            <p class="past-note">
              Những dòng này không duyệt được hoặc nằm ngoài khung giờ — phải đặt lại
              giờ hoặc gỡ khỏi lộ trình trước khi gọi AI.
              Giờ mới luôn nằm trong khung đã đặt ở trên.
            </p>

            <div v-if="showPastList" class="aff-list">
              <div v-for="row in needFixPreview" :key="row.id" class="aff-row">
                <span class="aff-when">{{ row.oldLabel }}</span>
                <span class="aff-name" :title="row.name">{{ row.name }}</span>
                <span class="aff-topic" :title="row.topic">{{ row.topic }}</span>
                <span class="aff-to is-drop">{{ row.note }}</span>
              </div>
            </div>

            <!-- Khoá cho tới khi khung giờ được chốt. Không khoá thì coach xếp lại
                 xong mới phát hiện khung sai, và phải làm lại từ đầu. -->
            <div v-if="!periodConfirmed" class="period-gate">
              Xác nhận khung giờ ở trên trước — giờ mới luôn được xếp trong khung đó.
            </div>

            <!-- Thứ tự theo mức độ đỡ việc cho coach: để AI làm hộ, tự làm, rồi mới tới
                 bỏ đi. "Bỏ qua" là đường một chiều (xoá khỏi lộ trình) nên xếp cuối. -->
            <div v-else class="aff-apply">
              <b-button
                variant="success"
                size="sm"
                :disabled="busy"
                title="AI chỉ xếp trong khung giờ đã chọn"
                @click="togglePastAi"
              >
                {{ showPastAi ? 'Đóng gợi ý AI' : 'AI gợi ý' }}
              </b-button>
              <b-button variant="outline-success" size="sm" :disabled="busy" @click="editPastItems">
                Nhập thủ công
              </b-button>
              <b-button size="sm" class="btn-skip" :disabled="busy" @click="skipPastItems">
                Bỏ qua {{ needFixItems.length }} nhắc nhở
              </b-button>
            </div>

            <!-- Xếp tay 80 dòng là việc không ai làm. AI lo phần giờ trong ngày, còn
                 NGÀY thì màn hình tự dời cả cụm — xem shiftPastDays. -->
            <TimeSuggestPanel
              v-if="showPastAi"
              v-model="aiPrompt"
              :scope-text="scopeText"
              :samples="AI_SAMPLES"
              :rows="aiRows"
              :loading="aiLoading"
              :busy="busy"
              :message="aiMessage"
              :shift-days="aiShiftDays"
              @fetch="fetchAiTimes"
              @apply="applyAiTimes"
            />
          </div>

        </div>

        <b-alert :show="plannedDuplicates > 0" variant="warning">
          Có <b>{{ plannedDuplicates }}</b> nhắc nhở trùng nhau (cùng giờ gửi và cùng nội
          dung gốc) — các dòng được tô vàng bên dưới.
        </b-alert>

        <!-- Lọc theo chủ đề. Chọn/xoá hàng loạt chỉ tác động lên dòng ĐANG hiện,
             không thì lọc xong bấm "Xoá đã chọn" lại xoá cả dòng đang bị ẩn. -->
        <div class="d-flex align-items-center mb-3" style="gap: 10px">
          <label class="mb-0 text-muted font-size-sm">Chủ đề</label>
          <b-dropdown variant="outline-secondary" size="sm">
            <template #button-content>{{ plannedTopicLabel }}</template>
            <b-dropdown-item
              v-for="opt in plannedTopicOptions"
              :key="opt.value || 'all'"
              :active="opt.value === plannedTopicFilter"
              @click="plannedTopicFilter = opt.value"
            >
              {{ opt.text }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <b-button
              variant="outline-danger"
              size="sm"
              :disabled="!selectedIds.length || busy"
              @click="handleBulkDelete"
            >
              Xoá đã chọn ({{ selectedIds.length }})
            </b-button>

            <b-button
              variant="success"
              size="sm"
              class="ml-2"
              :disabled="!dirtyPlannedIds.length || busy"
              @click="handleBulkSaveEdits"
            >
              Lưu thay đổi ({{ dirtyPlannedIds.length }})
            </b-button>
          </div>

          <b-button variant="success" size="sm" @click="openAddPlanned">
            Thêm nhắc nhở
          </b-button>
        </div>

        <div v-if="!planned.length" class="text-center text-muted py-10">
          Chương trình của gói này không có nhắc nhở nào hợp kênh đã chọn.
        </div>

        <div v-else-if="!plannedFiltered.length" class="text-center text-muted py-10">
          Không có nhắc nhở nào thuộc chủ đề đã chọn.
        </div>

        <div v-else class="table-responsive">
          <table class="table table-vertical-center">
            <thead>
              <tr>
                <th style="width: 36px">
                  <b-form-checkbox
                    :checked="isAllSelected"
                    :indeterminate="isPartiallySelected"
                    @change="toggleSelectAll"
                  />
                </th>
                <th style="width: 170px">Giờ gửi</th>
                <th style="width: 80px">Kênh</th>
                <th style="width: 200px">Hoạt động</th>
                <th style="width: 150px">Chủ đề</th>
                <th>Nội dung gốc</th>
                <th style="width: 100px">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in plannedFiltered"
                :key="`${item.id}-${index}`"
                :class="{ 'table-warning': item.duplicateCount > 0 }"
              >
                <td>
                  <b-form-checkbox
                    v-if="!item.isPlanOnly"
                    v-model="selectedIds"
                    :value="item.id"
                  />
                </td>
                <td>
                  <b-form-input
                    v-if="editingSet.has(item.id)"
                    v-model="editsById[item.id].sendAt"
                    type="datetime-local"
                    size="sm"
                  />
                  <template v-else>{{ formatDateTime(item.scheduledSendAt) }}</template>
                </td>
                <td>{{ item.channelName }}</td>
                <td>{{ item.activityName || '—' }}</td>
                <td class="topic-cell">
                  <span v-if="item.activityTopicName" class="topic-tag">
                    {{ item.activityTopicName }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="source-cell">
                  <b-form-textarea
                    v-if="editingSet.has(item.id)"
                    v-model="editsById[item.id].content"
                    rows="3"
                    size="sm"
                  />
                  <span v-else v-html="renderContent(item.sourceContent) || '—'"></span>

                  <!-- Ảnh của hoạt động. Khách nhận CẢ ảnh này lẫn phần chữ, nên xem
                       trước mà chỉ thấy chữ là xem thiếu đúng một nửa. Link chết thì
                       ẩn thẻ img, phần chữ không bị ảnh hưởng. -->
                  <a
                    v-if="isImageLink(item.detailLink)"
                    :href="item.detailLink"
                    target="_blank"
                    rel="noopener"
                    class="src-img-wrap"
                  >
                    <img
                      v-if="!badImages[item.detailLink]"
                      class="src-img"
                      :src="item.detailLink"
                      alt="Hình đi kèm"
                      @error="markBadImage(item.detailLink)"
                    />
                  </a>
                </td>
                <td>
                  <span v-if="item.isPlanOnly" class="text-muted font-size-sm">
                    Chưa ghi xuống
                  </span>
                  <b-button
                    v-else
                    :variant="editingSet.has(item.id) ? 'outline-secondary' : 'outline-primary'"
                    size="sm"
                    :disabled="busy"
                    @click="toggleEditRow(item)"
                  >
                    {{ editingSet.has(item.id) ? 'Đóng' : 'Sửa' }}
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-overlay>
    </b-modal>

    <b-modal
      v-model="showPlannedForm"
      title="Thêm nhắc nhở"
      size="lg"
      ok-title="Lưu"
      cancel-title="Huỷ"
      :ok-disabled="busy"
      @ok.prevent="handleSavePlanned"
    >
      <b-form-group label="Hoạt động">
        <basic-select
          placeholder="--- Chọn hoạt động ---"
          name="plannedActivity"
          :options="activityOptions"
          track-by="value"
          value-label="text"
          :value.sync="plannedForm.activity"
        />
      </b-form-group>

      <b-form-group label="Kênh gửi">
        <basic-select
          placeholder="Zalo"
          name="plannedChannel"
          :options="channelOptions"
          track-by="value"
          value-label="text"
          :value.sync="plannedForm.channel"
        />
      </b-form-group>

      <b-form-group label="Giờ gửi">
        <b-form-input v-model="plannedForm.sendAt" type="datetime-local" />
      </b-form-group>

      <b-form-group label="Nội dung gốc">
        <b-form-textarea v-model="plannedForm.content" rows="6" />
      </b-form-group>

    </b-modal>

    <b-modal
      v-model="showChangeTime"
      title="Đổi giờ gửi"
      ok-title="Lưu"
      cancel-title="Huỷ"
      :ok-disabled="busy || !newSendAt"
      @ok.prevent="handleChangeTime"
    >
      <p class="text-muted">
        Chỉ đổi giờ, không đặt lịch gửi. Nhắc nhở đã duyệt sẽ quay về "chờ duyệt" và
        lịch cũ bị huỷ — bấm "Tạo nhắc nhở" để đặt lại.
      </p>
      <b-form-input v-model="newSendAt" type="datetime-local" />
      <p v-if="changeTimeItem" class="text-muted font-size-sm mt-3 mb-0">
        Giờ hiện tại: {{ formatDateTime(changeTimeItem.scheduledSendAt) }}
      </p>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
// Mọi dropdown trong tính năng này đều là basic-select với cùng khuôn
// { value, text } — không trộn b-form-select hay radio group nữa.
// ReminderMethods bên BE: 1 = Zalo, 2 = App
const CHANNELS = [
  { value: 1, text: 'Zalo' },
  { value: 2, text: 'App' },
];

const USER_INFO_SOURCES = [
  { value: 0, text: 'Lấy từ hồ sơ trên app' },
  { value: 2, text: 'Khảo sát' },
  { value: 1, text: 'Tự nhập' },
];

const STEPS = [
  { n: 1, label: 'Chuẩn hoá Prompt' },
  { n: 2, label: 'Gọi AI' },
  { n: 3, label: 'Duyệt gợi ý' },
];

// UserPackageApprovalMode bên BE. 0 = duyệt từng tin (hỏi lại qua Zalo lúc gửi,
// portal chỉ đặt lịch hàng loạt) — ẩn thao tác/trạng thái từng dòng ở bước 3 vì
// quyết định thật sự nằm trên Zalo. 1 = duyệt trước nội dung (portal duyệt là
// chốt, gửi thẳng) — giữ nguyên UI duyệt từng dòng như cũ.
const APPROVAL_MODES = [
  { value: 0, text: 'Duyệt từng tin' },
  { value: 1, text: 'Duyệt trước nội dung' },
];

import reminderAiService, {
  REMINDER_TITLES,
  titleName,
  APPROVAL_STATUS,
  APPROVAL_STATUS_TEXT,
  APPROVAL_STATUS_VARIANT,
  REVIEW_ACTION,
} from '../services/reminderAiService';
import TimeSuggestPanel from './TimeSuggestPanel.vue';

// Gợi ý bấm sẵn. Coach không phải nghĩ cách diễn đạt, và cũng là cách nói cho họ
// biết prompt kiểu nào thì AI dùng được.
const AI_SAMPLES = [
  'Thuốc sau bữa sáng và bữa tối, vận động chiều mát',
  'Tránh giờ ngủ trưa 12:00–13:30',
  'Dồn vào sau giờ làm, từ 18:00 trở đi',
];

export default {
  name: 'ReminderAiModeTab',

  components: { TimeSuggestPanel },

  props: {
    userPackageId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      coachNote: '',
      coachNoteSaving: false,
      coachNoteSavedAt: '',
      REVIEW_ACTION,
      STEPS,
      // Bước đầu vào 1 (Chuẩn hoá Prompt); nếu gói đã có sẵn gợi ý (vào lại từ
      // "Tiếp tục AI Mode") thì nhảy thẳng bước 3 — làm 1 lần duy nhất mỗi khi
      // đổi userPackageId, xem watcher + loadData().
      currentStep: 1,
      stepInitialized: false,

      loading: false,
      generating: false,
      busy: false,

      loadingContext: false,
      context: {},

      showPrompt: false,
      loadingPrompt: false,
      promptPreview: '',
      promptMessage: '',

      showPlanned: false,
      loadingPlanned: false,
      // Coach đã xem qua danh sách hoạt động chưa. Nút Tạo gợi ý khoá cho tới khi rồi.
      plannedConfirmed: false,
      planned: [],

      // Khung giờ sinh hoạt. Mốc giờ KHÔNG cố định — đây chỉ là giá trị khởi tạo,
      // coach sửa được từng buổi và tick/bỏ tick độc lập.
      // detail_link không chắc là ảnh — có hoạt động gắn link bài viết. Ghi lại link
      // nào tải hỏng để đổi sang hiện chữ, thay vì để một ô trống không giải thích gì.
      badImages: {},

      showPeriodBox: true,
      // Coach đã chốt khung giờ chưa. Gói đã có cấu hình lưu sẵn thì coi như rồi
      // (xem hydratePeriods) — chỉ gói lần đầu mới phải bấm.
      periodConfirmed: false,

      // Mốc hỏi coach xác nhận lần cuối, lưu theo từng gói. Mặc định khớp đúng giá trị
      // BE dùng khi gói chưa đặt gì (DEFAULT_CONFIRM_LEAD/TIMEOUT_MINUTES) — lệch nhau
      // là ô trên màn hình nói một đằng, hệ thống chạy một nẻo.
      confirmLeadMinutes: 30,
      confirmTimeoutMinutes: 15,
      // Mặc định là một khoảng duy nhất; bốn buổi chỉ dùng khi coach tick chia nhỏ.
      splitByPeriod: false,
      rangeFrom: '06:00',
      rangeTo: '21:00',
      periods: [
        { key: 'morning', text: 'Sáng', from: '06:00', to: '11:00', enabled: false },
        { key: 'noon', text: 'Trưa', from: '11:00', to: '13:30', enabled: false },
        { key: 'afternoon', text: 'Chiều', from: '13:30', to: '17:30', enabled: false },
        { key: 'evening', text: 'Tối', from: '17:30', to: '21:30', enabled: false },
      ],
      // 0 = bỏ qua, 1 = tự nhập từng dòng, 2 = dồn vào một buổi cách nhau N phút
      AI_SAMPLES,
      // MỘT bộ state cho cả hai hộp (quá giờ / lệch khung). Chỉ một hộp mở khối AI tại
      // một thời điểm, nên tách đôi state chỉ tạo ra hai đường code lệch nhau dần.
      showPastAi: false,
      aiPrompt: '',
      aiLoading: false,
      aiMessage: '',
      // Kết quả lượt gợi ý gần nhất, đã ghép sẵn ngày: [{ id, name, reason,
      // oldLabel, newLabel, sendAt, fallback }]
      aiRows: [],
      aiShiftDays: 0,

      spacingMinutes: 10,
      savingPeriods: false,
      showAffected: true,
      showPastList: true,
      // Mốc "bây giờ" chốt lúc mở màn. Không đọc Date.now() thẳng trong computed —
      // computed không chạy lại khi đồng hồ nhích, sẽ đứng im ở giá trị đầu tiên.
      nowUnix: 0,

      activities: [],
      showPlannedForm: false,
      plannedForm: {
        activity: null,
        channel: CHANNELS[0],
        sendAt: '',
        content: '',
      },
      // Sửa hàng loạt: chọn nhiều dòng để xoá, hoặc bật inline-edit trên nhiều
      // dòng rồi bấm Lưu một lần — không còn modal, không còn gọi API từng dòng.
      selectedIds: [],
      editingIds: [],
      editsById: {},
      // Sửa gợi ý AI ngay ở bước 3 (bảng duyệt) — khác editingIds/editsById ở trên,
      // đó là sửa NỘI DUNG GỐC trong modal "Các hoạt động sẽ được nhắc" (bước 2).
      editingContentIds: [],
      contentEditsById: {},
      titleOverride: null,
      approvalModeOptions: APPROVAL_MODES,
      // Radio bind thẳng số, không giữ cả object như dropdown cũ
      approvalMode: APPROVAL_MODES[0].value,
      // Prompt theo từng nhóm xưng hô, nạp MỘT lần. null = chưa nạp xong.
      titlePrompts: null,
      // null = không lọc; ngược lại là tên chủ đề đang lọc
      plannedTopicFilter: null,
      // Tiến độ duyệt hàng loạt, dạng { done, total }. null = không chạy.
      // Vòng lặp duyệt đi tuần tự từng nhắc nhở nên đây là tiến độ THẬT.
      approveProgress: null,
      savingApprovalMode: false,
      metricKeys: [],
      // Mốc ngày (7/14/30/90) đưa vào prompt cho TỪNG chỉ số — { [key]: [7, 30, ...] }.
      // Mỗi chỉ số tự chọn mốc riêng, không bắt buộc đủ cả 4.
      metricWindowKeys: {},
      // Chỉ số nào đang mở rộng trong tree; null = không mở cái nào
      expandedMetric: null,
      // Mốc (7/14/30/90) nào đang mở tiếp để xem từng lần đo, dạng "key:days"
      expandedWindow: null,
      surveyQuestionIds: [],
      // Bộ khảo sát đang "mở" để chọn câu — bộ chưa có trong đây thì đóng, câu
      // của nó không tính vào prompt dù trước đó từng tích.
      selectedSurveyGroupIds: [],
      showSurvey: false,
      // Coach tự gõ mỗi lần, không lưu ở đâu cả
      surveyAnalysisPrompt: '',
      surveyAnalysisMessage: '',
      surveyInsight: '',
      analyzingSurvey: false,
      defaultsApplied: false,

      items: [],
      userInfoSourceOptions: USER_INFO_SOURCES,
      // multiselect: change-value-by-object nên đây là mảng OBJECT { value, text },
      // không phải mảng scalar — bind trực tiếp value/text ra basic-select mà
      // truyền scalar là nó không tự resolve lại thành object, hiện label rỗng.
      userInfoSource: [USER_INFO_SOURCES[0]],
      userInfoOverride: '',
      generateMessage: '',
      generateFailed: false,
      generateProgress: null,
      generateProgressTimer: null,

      showChangeTime: false,
      changeTimeItem: null,
      newSendAt: '',
    };
  },

  computed: {
    // Chỉ hiện chỉ số khách thực sự có dữ liệu; cái rỗng bày ra chỉ tổ rối
    // Chưa bấm gợi ý lần nào thì cây hoạt động chưa tồn tại trong DB
    // Tra cứu O(1) trong template: mỗi lần render bảng 80+ dòng gọi tới vài lần mỗi dòng
    editingSet() {
      return new Set(this.editingIds);
    },
    editingContentSet() {
      return new Set(this.editingContentIds);
    },
    // Giá trị gốc đã chuẩn hoá của từng dòng, chỉ tính lại khi danh sách đổi (không phải mỗi phím gõ)
    plannedBaseline() {
      const map = {};
      this.planned.forEach((item) => {
        map[item.id] = {
          content: this.toTagText(item.sourceContent).trim(),
          sendAt: this.toLocalInput(item.scheduledSendAt),
        };
      });
      return map;
    },
    isPlanOnly() {
      return this.context.isProgramTreeBuilt === false;
    },
    titleOptions() {
      return REMINDER_TITLES.map((t) => ({ value: t.value, text: t.text }));
    },
    titleValue() {
      return this.titleOverride?.value ?? this.titleOverride ?? null;
    },
    isTitleOverridden() {
      return (
        this.context.customerTitle !== null
        && this.context.customerTitle !== undefined
        && this.titleValue !== null
        && this.titleValue !== this.context.customerTitle
      );
    },
    availableMetrics() {
      return (this.context.metrics?.items || []).filter((m) => m.count90 > 0);
    },
    surveyGroups() {
      return (this.context.surveys?.items || []).filter((g) => g.sections?.length);
    },
    totalSurveyQuestions() {
      return this.surveyGroups.reduce((sum, g) => sum + (g.answeredCount || 0), 0);
    },
    // multiselect: chuẩn hoá về mảng giá trị scalar (basic-select lúc trả object lúc trả scalar)
    userInfoSourceValues() {
      return (this.userInfoSource || []).map((v) => v?.value ?? v);
    },
    // Lý do bước 1 chưa xong, rỗng = xong. Dùng luôn làm câu báo cho người dùng
    // chứ không để nút disable câm lặng, không ai biết vì sao.
    step1BlockReason() {
      // Sinh được gợi ý rồi thì bước 1 chắc chắn đã qua — vào lại gói cũ không
      // bắt làm lại từ đầu.
      if (this.items.length) return '';
      if (!this.userPackageId) return 'Chưa có gói để chạy AI Mode.';
      if (this.context.blocker) return this.context.blocker;
      if (!this.userInfoSourceValues.length) {
        return 'Chọn ít nhất một nguồn thông tin khách hàng đưa vào prompt.';
      }
      if (this.includesManualInfo && !this.userInfoOverride.trim()) {
        return 'Đã chọn "Tự nhập" nhưng chưa gõ thông tin khách hàng.';
      }
      if (this.includesSurveyInfo && !this.surveyQuestionIds.length && !this.surveyInsight) {
        return 'Đã chọn "Khảo sát" nhưng chưa chọn câu nào.';
      }
      return '';
    },
    step1Complete() {
      return !this.step1BlockReason;
    },
    step2Complete() {
      return this.items.length > 0;
    },
    // Bước xa nhất được phép mở — bước sau khoá tới khi bước trước xong
    maxStep() {
      if (!this.step1Complete) return 1;
      if (!this.step2Complete) return 2;
      return 3;
    },
    includesAppInfo() {
      return this.userInfoSourceValues.includes(0);
    },
    includesSurveyInfo() {
      return this.userInfoSourceValues.includes(2);
    },
    includesManualInfo() {
      return this.userInfoSourceValues.includes(1);
    },
    approvalModeValue() {
      return this.approvalMode?.value ?? this.approvalMode ?? 0;
    },

    // UserPackageApprovalMode: 0 = duyệt từng tin, 1 = duyệt trước nội dung.
    isPreApproved() {
      return this.approvalModeValue === 1;
    },
    // Duyệt từng tin (0): quyết định thật sự nằm trên Zalo lúc gửi, nên bảng ở bước 3
    // ẩn cột trạng thái + nút duyệt/bỏ qua từng dòng, chỉ còn nút "Duyệt tất cả" để
    // đặt lịch hàng loạt.
    showPerItemReview() {
      return this.approvalModeValue === 1;
    },
    // Payload gửi AI phải khớp đúng những nguồn coach đang tích ở "Thông tin khách
    // hàng đưa vào prompt" — bỏ tích nguồn nào thì KHÔNG gửi dữ liệu nguồn đó, dù
    // metricKeys/surveyQuestionIds vẫn còn giữ giá trị mặc định trong state (chỉ để
    // khôi phục lại UI khi coach tích lại, không phải để luôn gửi đi).
    generateSourcePayload() {
      return {
        metricKeys: this.includesAppInfo ? this.metricKeys : [],
        // Mốc ngày riêng của từng chỉ số — chỉ gửi cho các key đang thật sự chọn,
        // để BE khỏi phải đoán key nào còn hợp lệ.
        metricWindowDays: this.includesAppInfo
          ? this.metricKeys.reduce((acc, key) => {
            acc[key] = this.metricWindowKeys[key] || [7];
            return acc;
          }, {})
          : {},
        surveyQuestionIds: this.includesSurveyInfo ? this.surveyQuestionIds : [],
        surveyInsight: this.includesSurveyInfo ? this.surveyInsight : '',
      };
    },
    // Mỗi nhóm trùng đóng góp (n-1) bản thừa
    activityOptions() {
      return (this.activities || []).map((a) => ({
        value: a.id,
        text: `${a.name} (${a.reminderCount} nhắc nhở)`,
      }));
    },
    // Màn AI chỉ quản nhắc nhở Zalo — nhắc nhở App đi luồng thường và không hiện ở đây,
    // nên thêm mới cũng chỉ cho thêm Zalo. Để chọn App là tạo ra dòng lưu xong biến mất
    // khỏi danh sách. CHANNELS: 1 = Zalo, 2 = App, khớp ReminderMethods bên BE.
    channelOptions() {
      return CHANNELS.filter((x) => x.value === 1);
    },
    plannedTopicOptions() {
      const counts = new Map();
      this.planned.forEach((x) => {
        const key = x.activityTopicName || 'Không rõ chủ đề';
        counts.set(key, (counts.get(key) || 0) + 1);
      });

      return [
        { value: null, text: `Tất cả (${this.planned.length})` },
        ...[...counts.entries()]
          .sort((a, b) => a[0].localeCompare(b[0], 'vi'))
          .map(([name, count]) => ({ value: name, text: `${name} (${count})` })),
      ];
    },
    // Nhãn trên nút dropdown lấy lại đúng từ plannedTopicOptions, khỏi dựng chuỗi
    // "tên (số lượng)" ở hai nơi rồi lệch nhau
    plannedTopicLabel() {
      const opt = this.plannedTopicOptions.find((x) => x.value === this.plannedTopicFilter);
      return opt ? opt.text : 'Tất cả';
    },
    // Mốc 30 phút cho cả ngày, cộng thêm bất kỳ giá trị nào đang được dùng mà không
    // rơi đúng mốc (bản lưu cũ chẳng hạn) — thiếu nó thì select hiện trống trơn.
    clockOptions() {
      const base = [];
      for (let m = 0; m < 24 * 60; m += 30) {
        base.push(
          `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`,
        );
      }
      this.periods.forEach((p) => {
        if (p.from && !base.includes(p.from)) base.push(p.from);
        if (p.to && !base.includes(p.to)) base.push(p.to);
      });
      [this.rangeFrom, this.rangeTo].forEach((t) => {
        if (t && !base.includes(t)) base.push(t);
      });
      return base.sort();
    },

    // Khung giờ đang áp, để nói cho coach biết AI bị giới hạn trong cái gì.
    scopeText() {
      return this.enabledPeriods.map((p) => `${p.from}–${p.to}`).join(', ');
    },

    rangeReversed() {
      return this.isReversedPeriod({
        enabled: true, from: this.rangeFrom, to: this.rangeTo,
      });
    },

    // Nguồn duy nhất cho mọi tính toán phía dưới. Chế độ khoảng trả về một "buổi" giả
    // tên "Cả ngày" để isInsideEnabledPeriods không phải biết là đang ở chế độ nào —
    // hai nhánh code song song là hai chỗ để lệch nhau.
    enabledPeriods() {
      if (!this.splitByPeriod) {
        if (!this.rangeFrom || !this.rangeTo || this.rangeReversed) return [];
        return [{
          key: 'range',
          text: 'Cả ngày',
          from: this.rangeFrom,
          to: this.rangeTo,
          enabled: true,
        }];
      }

      return this.periods.filter(
        (p) => p.enabled && p.from && p.to && !this.isReversedPeriod(p),
      );
    },

    // Đã qua giờ gửi. Không liên quan gì tới khung giờ sinh hoạt — một nhắc nhở
    // 19:00 hôm qua vẫn "nằm trong khung Tối" nếu chỉ so giờ trong ngày.
    pastItems() {
      if (!this.nowUnix) return [];

      return this.planned.filter(
        (item) => !item.isPlanOnly && item.scheduledSendAt && item.scheduledSendAt <= this.nowUnix,
      );
    },

    // MỘT danh sách cho mọi dòng cần đặt lại giờ: quá giờ gửi, và lệch khung giờ.
    // Trước đây hai nhóm có hai bộ nút riêng, sửa nhóm trên xong vẫn phải sửa nhóm
    // dưới — cùng một việc mà bắt làm hai lượt.
    needFixItems() {
      const seen = new Set();
      return [...this.pastItems, ...this.outsideItems].filter((x) => {
        if (seen.has(x.id)) return false;
        seen.add(x.id);
        return true;
      });
    },

    needFixLabel() {
      const past = this.pastItems.length;
      const outside = this.outsideItems.length;

      if (past && outside) return `${past} nhắc nhở đã quá giờ, ${outside} lệch khung giờ`;
      if (past) return `${past} nhắc nhở đã quá giờ gửi`;
      return `${outside} nhắc nhở nằm ngoài khung giờ`;
    },

    needFixPreview() {
      const pastIds = new Set(this.pastItems.map((x) => x.id));

      return this.needFixItems.map((item) => ({
        id: item.id,
        name: item.activityName || 'Hoạt động không tên',
        topic: item.activityTopicName || '—',
        oldLabel: moment.unix(item.scheduledSendAt).format('DD/MM HH:mm'),
        note: pastIds.has(item.id)
          ? this.lateLabelOf(item.scheduledSendAt)
          : 'lệch khung giờ',
      }));
    },

    // Nhắc nhở nằm ngoài mọi khung đã tick. Bỏ dòng isPlanOnly (chưa có id thật) và
    // bỏ dòng đã quá giờ — chúng có khối cảnh báo riêng, gộp vào đây là coach dời
    // giờ trong ngày cho một dòng mà ngày của nó đã trôi qua từ lâu.
    outsideItems() {
      if (!this.enabledPeriods.length) return [];

      const pastIds = new Set(this.pastItems.map((x) => x.id));

      return this.planned.filter((item) => {
        if (item.isPlanOnly || !item.scheduledSendAt) return false;
        if (pastIds.has(item.id)) return false;
        return !this.isInsideEnabledPeriods(item.scheduledSendAt);
      });
    },

    plannedFiltered() {
      if (!this.plannedTopicFilter) return this.planned;

      return this.planned.filter(
        (x) => (x.activityTopicName || 'Không rõ chủ đề') === this.plannedTopicFilter,
      );
    },
    // Đếm trên danh sách ĐANG hiện, để khớp với câu "các dòng được tô vàng bên dưới"
    plannedDuplicates() {
      return this.plannedFiltered.reduce((sum, i) => sum + (i.duplicateCount > 0 ? 1 : 0), 0);
    },
    // Chỉ dòng đã ghi xuống DB mới chọn/xửa được — dòng isPlanOnly chưa có id thật.
    // Bám theo danh sách đang hiện: lọc xong "Xoá đã chọn" không được chạm dòng đang ẩn.
    selectableIds() {
      return this.plannedFiltered.filter((x) => !x.isPlanOnly).map((x) => x.id);
    },
    isAllSelected() {
      return (
        this.selectableIds.length > 0
        && this.selectedIds.length === this.selectableIds.length
      );
    },
    isPartiallySelected() {
      return this.selectedIds.length > 0 && !this.isAllSelected;
    },
    // So gia tri dang go trong editsById voi gia tri goc cua tung dong, chi nhung
    // dong THAT SU doi khac moi gui len khi bam Luu thay doi
    dirtyPlannedIds() {
      return this.planned
        .filter((item) => !item.isPlanOnly && this.editsById[item.id])
        .filter((item) => {
          const edit = this.editsById[item.id];
          const base = this.plannedBaseline[item.id];
          const contentChanged = this.toTagText(edit.content).trim() !== base.content;
          // So sánh CÙNG một dạng chuỗi 'YYYY-MM-DDTHH:mm' mà initEditsById đã gieo.
          // So bằng unix thì hỏng: toLocalInput cắt mất phần giây, nên một nhắc nhở
          // đặt lúc 14:30:07 luôn lệch 7 giây so với ô sửa và bị coi là đã đổi — dù
          // không ai chạm vào. Hậu quả: modal không đóng được bằng đường nào cả, kể
          // cả nút "Huỷ thay đổi", vì @hide thấy vẫn còn thay đổi chưa lưu.
          const sendAtChanged = edit.sendAt ? edit.sendAt !== base.sendAt : false;
          return contentChanged || sendAtChanged;
        })
        .map((item) => item.id);
    },
    pastDueCount() {
      return this.items.filter(
        (i) => i.status === APPROVAL_STATUS.PENDING && this.isPastDue(i),
      ).length;
    },
    pendingCount() {
      return this.items.filter((i) => i.status === APPROVAL_STATUS.PENDING).length;
    },
  },

  beforeDestroy() {
    this.stopGenerateProgressPolling();
  },

  watch: {
    // Đang đứng ở bước sau mà điều kiện của bước trước hỏng (bỏ hết nguồn thông
    // tin, xoá nháp...) thì kéo về bước xa nhất còn hợp lệ
    maxStep(val) {
      if (this.currentStep > val) this.currentStep = val;
    },
    // Đổi chủ đề đang lọc thì bỏ hết lựa chọn cũ: giữ lại là selectedIds còn trỏ tới
    // những dòng vừa bị ẩn, rồi "Xoá đã chọn" xoá đúng mấy dòng người dùng không thấy.
    plannedTopicFilter() {
      this.selectedIds = [];
    },

    // Vào Bước 2 là mở danh sách hoạt động, mỗi lần vào đều mở. Giờ gửi là thứ duy
    // nhất không sửa lại được sau khi AI chạy mà không phải sinh lại toàn bộ.
    currentStep(step) {
      if (step !== 2) return;

      this.plannedConfirmed = false;
      this.openPlanned();
    },

    // Đổi nhóm chỉ đổi prompt — chỉ số, khảo sát, số nhắc nhở đều không phụ thuộc
    // nhóm xưng hô. Có bảng prompt nạp sẵn thì áp tại chỗ, khỏi gọi lại getContext.
    titleValue(newVal, oldVal) {
      if (oldVal === null || newVal === oldVal) return;
      if (!this.applyTitleLocally(newVal)) this.loadContext();
    },
    userPackageId: {
      immediate: true,
      handler(id) {
        if (id) {
          // Đổi gói là đổi khách, lựa chọn và nhận định của gói cũ không còn ý nghĩa
          this.defaultsApplied = false;
          this.stepInitialized = false;
          this.currentStep = 1;
          this.surveyInsight = '';
          this.surveyAnalysisMessage = '';
          this.expandedMetric = null;
          this.expandedWindow = null;
          this.metricWindowKeys = {};
          this.loadContext();
          this.loadCoachNote();
          this.loadData();
          // Nạp một lần cho cả phiên; không phụ thuộc gói nên không cần nạp lại
          if (!this.titlePrompts) this.loadTitlePrompts();
        }
      },
    },

    // Chỉ số nào mới được tích (mặc định lúc nạp, hoặc coach tự tích thêm) mà
    // chưa có mốc riêng thì mặc định lấy 7 ngày — gần nhất, ít gây loãng prompt
    // nhất. Coach tự tích thêm mốc khác nếu cần.
    metricKeys(newKeys) {
      newKeys.forEach((key) => {
        if (!this.metricWindowKeys[key]) {
          this.$set(this.metricWindowKeys, key, [7]);
        }
      });
    },
  },

  methods: {
    async loadCoachNote() {
      this.coachNoteSavedAt = '';
      try {
        const data = await reminderAiService.getCoachNote(this.userPackageId);
        this.coachNote = (data && data.note && data.note !== '(không có)') ? data.note : '';
      } catch (error) {
        this.coachNote = '';
      }
    },
    async saveCoachNote() {
      this.coachNoteSaving = true;
      try {
        await reminderAiService.saveCoachNote({ userPackageId: this.userPackageId, note: this.coachNote });
        this.coachNoteSavedAt = moment().format('HH:mm');
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Lưu ghi chú thất bại' });
      } finally {
        this.coachNoteSaving = false;
      }
    },
    titleName,

    lockedReason(step) {
      if (step >= 2 && !this.step1Complete) return this.step1BlockReason;
      if (step >= 3 && !this.step2Complete) {
        return 'Chưa có gợi ý nào — bấm "Gợi ý từ AI" ở bước 2 trước.';
      }
      return '';
    },

    // Lùi lại thì tự do, tiến lên phải xong bước trước
    goToStep(step) {
      if (step > this.maxStep) {
        return this.$toastr.w({
          title: 'Chưa xong bước trước',
          msg: this.lockedReason(step),
        });
      }
      this.currentStep = step;
    },

    // Duyệt là lúc job được đặt, nên giờ gửi phải còn ở tương lai
    isPastDue(item) {
      return !!item.scheduledSendAt && moment.unix(item.scheduledSendAt).isBefore(moment());
    },

    // API trả DateTime ra dưới dạng Unix GIÂY. moment(số) hiểu là MILI giây,
    // dùng nhầm là mọi mốc thời gian nhảy về 1970.
    formatDateTime(value) {
      if (!value) return '—';
      const cache = this.$_fmtCache || (this.$_fmtCache = new Map());
      if (!cache.has(value)) cache.set(value, moment.unix(value).format('HH:mm DD/MM/YYYY'));
      return cache.get(value);
    },

    statusText(status) {
      return APPROVAL_STATUS_TEXT[status] || '—';
    },

    statusVariant(status) {
      return APPROVAL_STATUS_VARIANT[status] || 'secondary';
    },

    // Đã gửi hoặc hết hạn thì duyệt cũng không đổi được gì nữa
    canReview(item) {
      return (
        item.status === APPROVAL_STATUS.PENDING ||
        item.status === APPROVAL_STATUS.APPROVED ||
        item.status === APPROVAL_STATUS.SKIPPED
      );
    },

    // Nội dung trong DB dùng "dạng tag": xuống dòng lưu thành hai ký tự \ và n, đúng
    // như nội dung gốc của chương trình. Hai hàm dưới là hai chiều của cùng một phép đổi.
    //
    // renderContent: xem trước ĐÚNG những gì Zalo sẽ hiện. Màn này chỉ còn nhắc nhở
    // Zalo, mà Zalo KHÔNG hiểu thẻ HTML — chỉ hiểu \n. Thẻ HTML là dành cho thông báo
    // App. Render <b> thành chữ đậm ở đây là cho coach xem một thứ khác thứ khách nhận.
    // Bảng render lại mỗi phím gõ / mỗi lần cập nhật tiến độ: nhớ kết quả theo text (hàm thuần)
    renderContent(text) {
      if (!text) return '';
      const cache = this.$_renderCache || (this.$_renderCache = new Map());
      if (!cache.has(text)) {
        if (cache.size > 2000) cache.clear();
        cache.set(text, this.renderContentRaw(text));
      }
      return cache.get(text);
    },

    renderContentRaw(text) {
      // Bước 1: quy về dạng Zalo nhận được. Cùng quy tắc với NormalizeSource bên BE.
      // Phải xử lý cả hai dạng xuống dòng: nội dung gốc chương trình lưu tag "\n" hai
      // ký tự, còn bản AI trả về đã là xuống dòng thật sau khi parse JSON.
      const asZaloSees = text
        .replace(/\\r\\n|\\n/g, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p\s*>/gi, '\n\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

      // Bước 2: escape trước rồi mới tự thêm <br> — <br> là thẻ duy nhất được sinh ra,
      // nên v-html ở đây không mở đường cho XSS. Dùng <br> thay vì CSS pre-wrap vì hai
      // rule pre-wrap của file nằm dưới .reminder-ai-tab, mà b-modal render ra ngoài
      // body nên không với tới danh sách hoạt động.
      return asZaloSees
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    },

    // toTagText: dùng khi NẠP VÀO Ô SỬA và khi LƯU — gom cả xuống dòng thật (coach bấm
    // Enter) lẫn tag có sẵn về một dạng duy nhất, để dữ liệu lưu xuống luôn nhất quán.
    toTagText(text) {
      return (text || '').replace(/\r\n|\r|\n/g, '\\n');
    },

    canChangeTime(item) {
      return item.status !== APPROVAL_STATUS.SENT;
    },

    // Đã gửi cho khách thì xoá vô nghĩa — BE cũng chặn bằng reminder_already_sent
    canDeleteItem(item) {
      return item.status !== APPROVAL_STATUS.SENT
        && !!item.tranUserPackageActivityReminderId;
    },

    async handleDeleteItem(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Xoá nhắc nhở ${item.code}? Nhắc nhở này sẽ bị gỡ khỏi gói và không được gửi.`,
        {
          title: 'Xác nhận',
          okTitle: 'Xoá',
          okVariant: 'danger',
          cancelTitle: 'Huỷ',
          cancelVariant: 'light',
        },
      );
      if (!confirmed) return;

      this.busy = true;
      try {
        // Cùng endpoint với "Xoá đã chọn" ở modal hoạt động — nó nhận id của
        // tran_user_package_activity_reminder, không phải id bản ghi duyệt.
        await reminderAiService.deletePlanned(item.tranUserPackageActivityReminderId);
        this.$toastr.s({ title: 'Thành công !', msg: `Đã xoá ${item.code}` });
        await this.loadData();
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Xoá thất bại' });
      } finally {
        this.busy = false;
      }
    },

    isZaloItem(item) {
      return item.activityReminderMethod === 1;
    },

    // Chỉ nhắc nhở gửi vào nhóm Zalo mới đi qua bước hỏi coach xác nhận; thông báo App
    // thì duyệt ở đây là chốt. Nói rõ ra để coach không tưởng mọi dòng đều còn cửa sửa.
    channelNote(item) {
      if (!this.isZaloItem(item)) return 'duyệt là gửi';
      return this.showPerItemReview ? 'duyệt là gửi' : 'chờ coach xác nhận';
    },

    toggleMetricExpand(key) {
      this.expandedMetric = this.expandedMetric === key ? null : key;
      this.expandedWindow = null;
    },

    metricWindows(m) {
      return [
        { days: 7, count: m.count7, average: m.average7 },
        { days: 14, count: m.count14, average: m.average14 },
        { days: 30, count: m.count30, average: m.average30 },
        { days: 90, count: m.count90, average: m.average90 },
      ];
    },

    toggleWindowExpand(key, days) {
      const id = `${key}:${days}`;
      this.expandedWindow = this.expandedWindow === id ? null : id;
    },

    // Mốc nào tích thì mốc đó vào prompt cho CHỈ SỐ NÀY — mỗi chỉ số tự chọn
    // mốc riêng (7/14/30/90), không bắt buộc đủ cả 4.
    isWindowSelected(key, days) {
      return (this.metricWindowKeys[key] || []).includes(days);
    },

    toggleWindowSelected(key, days) {
      const current = this.metricWindowKeys[key] || [];
      const next = current.includes(days)
        ? current.filter((d) => d !== days)
        : [...current, days];
      this.$set(this.metricWindowKeys, key, next);
    },

    // entries đã là cả 90 ngày — lọc lại theo từng mốc ngay trên FE, khỏi gọi thêm API
    entriesForWindow(entries, days) {
      const cutoff = moment().subtract(days, 'days');
      return (entries || []).filter((e) => moment(e.at).isSameOrAfter(cutoff));
    },

    // entries.at là DateTime thường (không phải unix giây như scheduledSendAt), parse
    // thẳng bằng moment — khác formatDateTime() ở dưới đang dùng cho unix giây.
    formatEntryDateTime(at) {
      return moment(at).format('HH:mm DD/MM/YYYY');
    },

    async handlePreviewPrompt() {
      this.showPrompt = true;
      this.loadingPrompt = true;
      this.promptPreview = '';
      this.promptMessage = '';
      try {
        const result = await reminderAiService.generate({
          userPackageId: this.userPackageId,
          userInfoOverride:
            this.includesManualInfo ? this.userInfoOverride : null,
          ...this.generateSourcePayload,
          titleOverride: this.titleValue,
          previewOnly: true,
        });
        this.promptPreview = result?.promptPreview || '';
        this.promptMessage = result?.message || '';
      } catch (error) {
        this.promptMessage = error.message || 'Không dựng được prompt';
      } finally {
        this.loadingPrompt = false;
      }
    },

    // Nhận Unix giây từ API, ô datetime-local cần chuỗi local
    // ===== Khung giờ sinh hoạt =====

    minutesOfClock(hhmm) {
      const [h, m] = String(hhmm || '').split(':');
      return Number(h) * 60 + Number(m || 0);
    },

    // moment.from() trả tiếng Anh vì portal không set locale 'vi' toàn cục
    // (chỉ vài chỗ gọi .locale('vi') tại chỗ). Tự dựng nhãn cho khỏi lẫn ngôn ngữ.
    lateLabelOf(unixSeconds) {
      const mins = Math.max(0, Math.round((this.nowUnix - unixSeconds) / 60));
      if (mins < 60) return `trễ ${mins} phút`;

      const hours = Math.floor(mins / 60);
      if (hours < 24) return `trễ ${hours} giờ`;

      return `trễ ${Math.floor(hours / 24)} ngày`;
    },

    // Khung "ngược" (giờ đầu >= giờ cuối) bị loại khỏi tính toán, nhưng phải BÁO ra
    // ở thẻ tương ứng — âm thầm bỏ qua thì coach tick buổi xong không hiểu vì sao
    // không có nhắc nhở nào được dời.
    isReversedPeriod(p) {
      return !!(p.enabled && p.from && p.to
        && this.minutesOfClock(p.from) >= this.minutesOfClock(p.to));
    },

    isInsideEnabledPeriods(unixSeconds) {
      const minutes = moment.unix(unixSeconds).hours() * 60 + moment.unix(unixSeconds).minutes();
      return this.enabledPeriods.some((p) => {
        const from = this.minutesOfClock(p.from);
        const to = this.minutesOfClock(p.to);
        return minutes >= from && minutes <= to;
      });
    },

    async savePeriods() {
      if (!this.userPackageId) return;

      // Chờ một nhịp: @change của checkbox/select bắn TRƯỚC khi v-model kịp ghi
      // giá trị mới, lưu ngay là lưu nhầm giá trị cũ.
      await this.$nextTick();

      this.savingPeriods = true;
      try {
        await reminderAiService.setActivePeriods({
          userPackageId: this.userPackageId,
          preference: {
            splitByPeriod: this.splitByPeriod,
            rangeFrom: this.rangeFrom,
            rangeTo: this.rangeTo,
            periods: this.periods.map((p) => ({
              key: p.key,
              from: p.from,
              to: p.to,
              enabled: p.enabled,
            })),
            confirmLeadMinutes: this.confirmLeadMinutes,
            confirmTimeoutMinutes: this.confirmTimeoutMinutes,
            spacingMinutes: this.spacingMinutes,
          },
        });
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không lưu được khung giờ sinh hoạt',
        });
      } finally {
        this.savingPeriods = false;
      }
    },

    // Đổ cấu hình đã lưu vào form. Thiếu buổi nào trong bản lưu thì giữ mốc mặc định
    // của buổi đó — bản lưu cũ hơn không được làm mất buổi mới thêm.
    hydratePeriods(pref) {
      if (!pref) return;

      this.splitByPeriod = !!pref.splitByPeriod;
      if (pref.rangeFrom) this.rangeFrom = pref.rangeFrom;
      if (pref.rangeTo) this.rangeTo = pref.rangeTo;

      if (Array.isArray(pref.periods)) {
        this.periods = this.periods.map((p) => {
          const saved = pref.periods.find((x) => x.key === p.key);
          return saved
            ? { ...p, from: saved.from || p.from, to: saved.to || p.to, enabled: !!saved.enabled }
            : p;
        });
      }

      if (pref.confirmLeadMinutes) this.confirmLeadMinutes = pref.confirmLeadMinutes;
      if (pref.confirmTimeoutMinutes) this.confirmTimeoutMinutes = pref.confirmTimeoutMinutes;
      if (pref.spacingMinutes) this.spacingMinutes = pref.spacingMinutes;

      // Có bản lưu nghĩa là khung này từng được coach đặt, khỏi bắt bấm lại mỗi lần
      // mở màn. Chỉ gói chưa từng cấu hình mới đi qua cổng xác nhận.
      this.periodConfirmed = true;
    },

    reopenPeriods() {
      this.periodConfirmed = false;
      this.showPeriodBox = true;
    },

    async confirmPeriods() {
      await this.savePeriods();
      this.periodConfirmed = true;

      // Có dòng cần xếp lại thì gọi AI xếp trong khung vừa chốt luôn, coach chỉ việc xác nhận lưu
      if (this.needFixItems.length) {
        this.showPastAi = true;
        this.resetAi();
        await this.fetchAiTimes();
      }
    },

    // Mở ô sửa inline cho một loạt dòng. Dùng chung cho cả nhánh lệch khung lẫn
    // nhánh quá giờ — hai chỗ cùng cần đúng một việc.
    openEditRows(items) {
      items.forEach((item) => {
        if (!this.editsById[item.id]) {
          this.$set(this.editsById, item.id, {
            content: item.sourceContent || '',
            sendAt: this.toLocalInput(item.scheduledSendAt),
          });
        }
        if (!this.editingIds.includes(item.id)) this.editingIds.push(item.id);
      });
    },

    togglePastAi() {
      this.showPastAi = !this.showPastAi;

      this.resetAi();
    },

    // detail_link phần lớn KHÔNG phải URL: nó giữ đích deeplink của app (ScheduleMenuFood,
    // ScheduleBloodSugar…) để thông báo App bấm vào thì mở đúng màn. Chỉ số ít hoạt động
    // điền URL ảnh vào đó. Không lọc thì bảng đổ ra một cột tên màn hình xanh lè, coach
    // tưởng là link bấm được.
    isImageLink(link) {
      return !!link && /^https?:\/\//i.test(link) && !this.badImages[link];
    },

    markBadImage(link) {
      this.$set(this.badImages, link, true);
    },

    resetAi() {
      this.aiRows = [];
      this.aiMessage = '';
      this.aiShiftDays = 0;
    },

    // Danh sách đang được xếp. Một hàm quyết định, không rải rác ba chỗ tự đoán.
    aiItems() {
      return this.needFixItems;
    },

    async fetchAiTimes() {
      const items = this.aiItems();
      if (!items.length) return;

      // Nhóm ngày ẩn danh: AI cần biết dòng nào cùng ngày để giãn ra, nhưng không
      // cần biết đó là ngày nào. Không gửi ngày thật thì nó không có gì để sửa.
      const days = [...new Set(
        items.map((x) => moment.unix(x.scheduledSendAt).format('YYYY-MM-DD')),
      )].sort();
      const dayKeyOf = new Map(days.map((d, i) => [d, i + 1]));

      this.aiLoading = true;
      this.aiMessage = '';

      try {
        const res = await reminderAiService.suggestTimes({
          userPackageId: this.userPackageId,
          prompt: this.aiPrompt || '',
          minGapMinutes: Number(this.spacingMinutes) || 10,
          periods: this.enabledPeriods.map((p) => ({
            key: p.key,
            from: p.from,
            to: p.to,
            enabled: true,
          })),
          items: items.map((x) => ({
            id: x.id,
            activityName: x.activityName || 'Hoạt động không tên',
            topicName: x.activityTopicName || '',
            currentTime: moment.unix(x.scheduledSendAt).format('HH:mm'),
            dayKey: dayKeyOf.get(moment.unix(x.scheduledSendAt).format('YYYY-MM-DD')) || 1,
          })),
        });

        this.buildAiRows(items, res);
      } catch (e) {
        this.resetAi();
        this.$toastr.e({
          title: 'Không lấy được gợi ý',
          msg: (e && e.message) || 'Gọi AI thất bại',
        });
      } finally {
        this.aiLoading = false;
      }
    },

    // Ghép giờ AI trả về với ngày. AI chỉ đưa "HH:mm"; ngày do đây quyết, và quy tắc
    // là DỜI CẢ CỤM cùng một số ngày sao cho mốc sớm nhất vừa vượt hiện tại. Dời đều
    // thì khoảng cách giữa các mốc giữ nguyên, nhịp lộ trình không đổi — khác hẳn
    // việc dồn tất cả về hôm nay, thứ sẽ bắn cả tuần nhắc nhở vào một buổi chiều.
    buildAiRows(items, res) {
      const byId = new Map(
        ((res && res.items) || []).map((x) => [x.id, x]),
      );

      if (!byId.size) {
        this.resetAi();
        this.aiMessage = (res && res.message) || 'AI không xếp được dòng nào.';
        return;
      }

      const dayOf = (item) => moment.unix(item.scheduledSendAt).format('YYYY-MM-DD');
      const compose = (item, shift) => {
        const hit = byId.get(item.id);
        const time = (hit && hit.time) || moment.unix(item.scheduledSendAt).format('HH:mm');
        return moment(`${dayOf(item)} ${time}`, 'YYYY-MM-DD HH:mm').add(shift, 'day');
      };

      const sorted = [...items].sort((a, b) => a.scheduledSendAt - b.scheduledSendAt);

      // Chừa 5 phút: đặt job sát hiện tại quá thì tới lúc coach bấm Lưu nó đã thành
      // quá khứ và BE trả lại đúng lỗi "giờ gửi đã qua" mà ta đang đi gỡ.
      const limit = moment().add(5, 'minutes');
      let shift = 0;
      while (compose(sorted[0], shift).isBefore(limit) && shift < 400) shift += 1;

      this.aiShiftDays = shift;
      this.aiMessage = (res && res.message) || '';

      this.aiRows = sorted.map((item) => {
        const hit = byId.get(item.id);
        const next = compose(item, shift);

        return {
          id: item.id,
          name: item.activityName || 'Hoạt động không tên',
          reason: (hit && hit.reason) || '—',
          fallback: !!(hit && hit.fallback),
          oldLabel: moment.unix(item.scheduledSendAt).format('DD/MM HH:mm'),
          newLabel: next.format('DD/MM HH:mm'),
          sendAt: next.format('YYYY-MM-DDTHH:mm'),
        };
      });
    },

    // Ghi xuống LUÔN, không dừng ở bước điền sẵn. Coach đã xem bảng trước/sau rồi mới
    // bấm Áp dụng — bắt bấm thêm "Lưu thay đổi" là một lượt xác nhận cho thứ vừa xác nhận.
    async applyAiTimes() {
      if (!this.aiRows.length) return;

      this.openEditRows(this.aiItems());
      this.aiRows.forEach((row) => {
        if (this.editsById[row.id]) this.editsById[row.id].sendAt = row.sendAt;
      });

      const count = this.aiRows.length;
      await this.handleBulkSaveEdits();

      if (this.dirtyPlannedIds.length) return;

      this.showPastAi = false;
      this.resetAi();

      this.$toastr.s({
        title: 'Đã lưu',
        msg: `Đã xếp lại và ghi xuống ${count} nhắc nhở`,
      });
    },

    async skipPastItems() {
      if (!this.needFixItems.length) return;
      this.selectedIds = this.needFixItems.map((x) => x.id);
      await this.handleBulkDelete();
    },

    editPastItems() {
      if (!this.needFixItems.length) return;
      this.openEditRows(this.needFixItems);
      this.$toastr.s({
        title: 'Đã mở ô sửa',
        msg: `Nhập giờ mới cho ${this.needFixItems.length} nhắc nhở rồi bấm "Lưu thay đổi"`,
      });
    },

    toLocalInput(unixSeconds) {
      return unixSeconds
        ? moment.unix(unixSeconds).format('YYYY-MM-DDTHH:mm')
        : moment().add(1, 'day').format('YYYY-MM-DDTHH:mm');
    },

    async openAddPlanned() {
      this.plannedForm = {
        activity: null,
        channel: CHANNELS[0],
        sendAt: this.toLocalInput(null),
        content: '',
      };

      if (!this.activities.length) {
        try {
          this.activities =
            (await reminderAiService.getPackageActivities(this.userPackageId)) || [];
        } catch (error) {
          this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message || 'Không tải được danh sách hoạt động',
          });
        }
      }

      this.showPlannedForm = true;
    },

    async handleSavePlanned() {
      const sendAtUnix = moment(this.plannedForm.sendAt).unix();

      if (!this.plannedForm.content || !this.plannedForm.content.trim()) {
        return this.$toastr.e({ title: 'Lỗi !', msg: 'Nội dung không được để trống' });
      }

      const activityId = this.plannedForm.activity?.value ?? this.plannedForm.activity;

      if (!activityId) {
        return this.$toastr.e({ title: 'Lỗi !', msg: 'Chọn hoạt động trước' });
      }

      this.busy = true;
      try {
        await reminderAiService.addPlanned({
          userPackageId: this.userPackageId,
          tranProgramActivityId: activityId,
          content: this.toTagText(this.plannedForm.content),
          sendAt: sendAtUnix,
          activityReminderMethod:
            this.plannedForm.channel?.value ?? this.plannedForm.channel,
        });

        this.showPlannedForm = false;
        this.$toastr.s({ title: 'Thành công !', msg: 'Đã lưu nhắc nhở' });

        // Thêm đổi cả danh sách lẫn số đếm trên nút, nạp lại cả ba
        await Promise.all([this.openPlannedReload(), this.loadContext(), this.loadData()]);
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Lưu thất bại' });
      } finally {
        this.busy = false;
      }
    },

    // Mỗi dòng được đưa vào editsById ngay khi đọc danh sách, kể cả dòng chưa bật
    // sửa — nhờ vậy v-model trên ô inline-edit luôn có chỗ để gán, không phải
    // đợi đến lúc bấm "Sửa" mới tạo key (vướng reactivity của Vue 2 với object).
    initEditsById() {
      const map = {};
      this.planned.forEach((item) => {
        map[item.id] = {
          content: this.toTagText(item.sourceContent),
          sendAt: this.toLocalInput(item.scheduledSendAt),
        };
      });
      this.editsById = map;
    },

    resetPlannedSelection() {
      this.plannedTopicFilter = null;
      this.selectedIds = [];
      this.editingIds = [];
      this.initEditsById();
    },

    toggleSelectAll(checked) {
      this.selectedIds = checked ? [...this.selectableIds] : [];
    },

    // Bật/tắt ô sửa inline cho một dòng. Đóng lại thì khoá về giá trị gốc —
    // giống kiểu "Huỷ" của modal cũ, không âm thầm giữ bản gõ dở.
    toggleEditRow(item) {
      const idx = this.editingIds.indexOf(item.id);
      if (idx === -1) {
        this.editingIds.push(item.id);
        return;
      }

      this.editingIds.splice(idx, 1);
      this.$set(this.editsById, item.id, {
        content: item.sourceContent || '',
        sendAt: this.toLocalInput(item.scheduledSendAt),
      });
    },

    async handleBulkDelete() {
      const count = this.selectedIds.length;
      if (!count) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Xoá ${count} nhắc nhở đã chọn? Các tin này sẽ không được gửi qua cho AI.`,
        {
          title: 'Xác nhận',
          okTitle: 'Xoá',
          okVariant: 'danger',
          cancelTitle: 'Huỷ',
          cancelVariant: 'light',
        },
      );
      if (!confirmed) return;

      this.busy = true;
      try {
        const result = await reminderAiService.bulkDeletePlanned({
          userPackageId: this.userPackageId,
          ids: this.selectedIds,
        });

        this.reportBulkResult(result, 'xoá', count - (result?.failed?.length || 0));
        await Promise.all([this.openPlannedReload(), this.loadContext(), this.loadData()]);
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Xoá thất bại' });
      } finally {
        this.busy = false;
      }
    },

    async handleBulkSaveEdits() {
      const ids = this.dirtyPlannedIds;
      if (!ids.length) return;

      const items = ids.map((id) => ({
        id,
        content: this.toTagText(this.editsById[id].content),
        sendAt: moment(this.editsById[id].sendAt).unix(),
      }));

      this.busy = true;
      try {
        const result = await reminderAiService.bulkUpdatePlanned({
          userPackageId: this.userPackageId,
          items,
        });

        this.reportBulkResult(result, 'lưu', (result?.items || []).length);
        await Promise.all([this.openPlannedReload(), this.loadContext(), this.loadData()]);
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Lưu thất bại' });
      } finally {
        this.busy = false;
      }
    },

    // Dùng chung cho cả sửa và xoá hàng loạt: một lô có thể vừa có dòng lưu được
    // vừa có dòng bị chặn (ví dụ đã gửi cho khách) — phải báo rõ cả hai, không
    // được coi im lặng là thành công hết.
    reportBulkResult(result, action, successCount) {
      const failed = result?.failed || [];

      if (failed.length === 0) {
        this.$toastr.s({ title: 'Thành công !', msg: `Đã ${action} ${successCount} nhắc nhở` });
        return;
      }

      const verb = action === 'xoá' ? 'Xoá' : 'Lưu';
      this.$toastr.w({
        title: 'Một phần thất bại',
        msg: `${verb} được ${successCount}, ${failed.length} dòng lỗi: `
          + failed.map((f) => f.message).join('; '),
      });
    },

    // Chốt lại mốc "bây giờ" mỗi lần danh sách được nạp: modal mở lâu thì mốc cũ
    // kể như sai, mà mở lại sau khi xoá/sửa cũng phải tính lại cho đúng.
    stampNow() {
      this.nowUnix = moment().unix();
    },

    async openPlannedReload() {
      this.planned = (await reminderAiService.getPlanned(this.userPackageId)) || [];
      this.stampNow();
      this.resetPlannedSelection();
    },

    // Gộp câu hỏi của mọi phần trong một bộ
    groupQuestionIds(group) {
      return (group.sections || []).flatMap((sec) => sec.lines.map((l) => l.questionId));
    },

    isGroupFullySelected(group) {
      const ids = this.groupQuestionIds(group);
      return ids.length > 0 && ids.every((id) => this.surveyQuestionIds.includes(id));
    },

    toggleSurveyGroup(group) {
      this.applySelection(this.groupQuestionIds(group), !this.isGroupFullySelected(group));
    },

    isSurveyGroupActive(group) {
      return this.selectedSurveyGroupIds.includes(group.surveyId);
    },

    // Mở bộ thì mặc định lấy hết câu của bộ (coach tự bớt sau); đóng bộ thì bỏ
    // hết câu của bộ đó — không để câu của bộ đã đóng lọt vào prompt.
    toggleSurveyGroupActive(group) {
      const willActivate = !this.isSurveyGroupActive(group);
      this.selectedSurveyGroupIds = willActivate
        ? [...this.selectedSurveyGroupIds, group.surveyId]
        : this.selectedSurveyGroupIds.filter((id) => id !== group.surveyId);

      this.applySelection(this.groupQuestionIds(group), willActivate);
    },

    isSectionFullySelected(section) {
      return (
        section.lines.length > 0
        && section.lines.every((l) => this.surveyQuestionIds.includes(l.questionId))
      );
    },

    toggleSurveySection(section) {
      const ids = section.lines.map((l) => l.questionId);
      this.applySelection(ids, !this.isSectionFullySelected(section));
    },

    async handleAnalyzeSurvey() {
      this.analyzingSurvey = true;
      this.surveyAnalysisMessage = '';
      try {
        const result = await reminderAiService.analyzeSurvey({
          userPackageId: this.userPackageId,
          surveyQuestionIds: this.surveyQuestionIds,
          prompt: this.surveyAnalysisPrompt,
        });

        this.surveyInsight = result?.insight || '';
        this.surveyAnalysisMessage = result?.message || '';
      } catch (error) {
        this.surveyAnalysisMessage = error.message || 'Phân tích thất bại';
      } finally {
        this.analyzingSurvey = false;
      }
    },

    applySelection(ids, selected) {
      this.surveyQuestionIds = selected
        ? [...new Set([...this.surveyQuestionIds, ...ids])]
        : this.surveyQuestionIds.filter((id) => !ids.includes(id));
    },

    // Gọi khi modal đóng, bằng nút Xác nhận hay bằng dấu X đều tính là đã xem.
    confirmPlanned() {
      this.plannedConfirmed = true;
    },

    // "Áp dụng" ở khối khung giờ chỉ ĐIỀN vào ô sửa, chưa ghi xuống DB. Nên nút
    // xác nhận phải lưu trước rồi mới đóng — không thì coach dời giờ xong bấm
    // Xác nhận, modal đóng, và AI vẫn nhận nguyên mốc giờ cũ.
    async confirmAndClose() {
      if (this.dirtyPlannedIds.length) {
        await this.handleBulkSaveEdits();
        // Lưu hỏng thì giữ modal lại, đừng đóng rồi báo thành công giả
        if (this.dirtyPlannedIds.length) return;
      }
      this.showPlanned = false;
    },

    discardPlannedEdits() {
      this.editingIds = [];
      this.initEditsById();
      this.showPlanned = false;
    },

    // Chặn mọi đường đóng khác (dấu X, bấm ra ngoài, phím Esc) khi còn thay đổi
    // chưa lưu. Mất công dời 12 dòng rồi bấm nhầm ra ngoài là mất sạch.
    onPlannedHide(evt) {
      if (!this.dirtyPlannedIds.length) return;

      evt.preventDefault();
      this.$toastr.w({
        title: 'Chưa lưu',
        msg: `Còn ${this.dirtyPlannedIds.length} thay đổi giờ gửi — bấm "Lưu & xác nhận" hoặc "Huỷ thay đổi".`,
      });
    },

    async openPlanned() {
      this.showPlanned = true;
      this.loadingPlanned = true;
      try {
        // Gói AI Mode vừa tạo thì cây hoạt động chưa có dưới DB, mà sửa / xoá thì phải
        // có dòng thật để trỏ tới. Chốt luôn lúc mở màn để sửa được TRƯỚC khi gọi AI.
        this.planned = this.isPlanOnly
          ? (await reminderAiService.preparePlanned(this.userPackageId)) || []
          : (await reminderAiService.getPlanned(this.userPackageId)) || [];

        this.stampNow();
        this.resetPlannedSelection();

        if (this.isPlanOnly) await this.loadContext();
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không tải được danh sách hoạt động',
        });
      } finally {
        this.loadingPlanned = false;
      }
    },

    // @input chỉ bắn khi coach thật sự đổi lựa chọn trên UI — set lập trình từ
    // loadContext() không đi qua đường này nên không lo gọi lưu thừa lúc mới nạp.
    // b-form-radio-group phát @change kèm giá trị mới. Nhận qua tham số chứ không đọc
    // lại state, để không phụ thuộc vào thứ tự chạy giữa listener của v-model và cái này.
    async handleApprovalModeChange(value) {
      if (!this.userPackageId) return;

      const approvalMode = typeof value === 'number' ? value : this.approvalModeValue;

      this.savingApprovalMode = true;
      try {
        await reminderAiService.setApprovalMode({
          userPackageId: this.userPackageId,
          approvalMode,
        });
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Lưu cách duyệt thất bại',
        });
      } finally {
        this.savingApprovalMode = false;
      }
    },

    // Trả false khi không tự quyết được (chưa nạp bảng prompt, hoặc nhóm mới không có
    // prompt) — lúc đó chỗ gọi rơi về loadContext để lấy đúng câu thông báo của BE,
    // vì câu đó nhắc tên khoá con trong Tham số hệ thống mà FE không suy ra được.
    applyTitleLocally(titleValue) {
      if (!this.titlePrompts) return false;

      const prompt = this.titlePrompts[titleValue];
      if (!prompt) return false;

      this.$set(this.context, 'stylePrompt', prompt);

      // Chỉ gỡ đúng blocker do thiếu prompt; blocker vì gói không có nhắc nhở thì giữ
      if ((this.context.blocker || '').includes('chưa có prompt')) {
        this.$set(this.context, 'blocker', null);
      }

      return true;
    },

    async loadTitlePrompts() {
      try {
        this.titlePrompts = (await reminderAiService.getTitlePrompts()) || {};
      } catch (error) {
        // Nạp hỏng thì thôi, đổi nhóm rơi về đường cũ (gọi lại getContext)
        this.titlePrompts = null;
      }
    },

    async loadContext() {
      if (!this.userPackageId) return;

      this.loadingContext = true;
      try {
        this.context =
          (await reminderAiService.getContext(this.userPackageId, this.titleValue)) || {};

        // Đồng bộ đúng giá trị đã lưu trên UserPackage mỗi lần nạp lại — đây là cấu
        // hình đã chốt, không phải lựa chọn tạm trong phiên như metricKeys/khảo sát.
        this.approvalMode = this.approvalModeOptions
          .some((o) => o.value === this.context.approvalMode)
          ? this.context.approvalMode
          : this.approvalModeOptions[0].value;

        this.hydratePeriods(this.context.activePeriods);

        // Lần nạp đầu chưa chọn gì thì lấy nhóm của khách làm mặc định
        if (this.titleOverride === null && this.context.title !== null) {
          this.titleOverride = this.titleOptions.find(
            (o) => o.value === this.context.title,
          );
        }

        // Chỉ đặt mặc định ở lần nạp đầu. Hàm này còn chạy lại mỗi lần đổi nhóm
        // xưng hô và sau khi sinh gợi ý — đặt lại là xoá sạch những gì người dùng
        // vừa tích trong modal khảo sát.
        if (!this.defaultsApplied) {
          this.defaultsApplied = true;

          // Chỉ số: tích cái còn hoạt động trong 30 ngày — cái 90 ngày mới có một
          // lần thì đưa vào prompt cũng không nói lên điều gì
          this.metricKeys = this.availableMetrics
            .filter((m) => m.count30 > 0)
            .map((m) => m.key);

          // Khảo sát thì mở hết bộ + tích hết câu rồi để người dùng bớt — ngược
          // với chỉ số: không xem qua câu trả lời thì không biết câu nào đáng giữ
          this.selectedSurveyGroupIds = this.surveyGroups.map((g) => g.surveyId);
          this.surveyQuestionIds = this.surveyGroups
            .flatMap((g) => this.groupQuestionIds(g));
        }
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không đọc được cấu hình gợi ý',
        });
      } finally {
        this.loadingContext = false;
      }
    },

    async loadData() {
      if (!this.userPackageId) return;

      this.loading = true;
      try {
        this.items = (await reminderAiService.getByPackage(this.userPackageId)) || [];

        // Chỉ tự chọn bước một lần: vào lại gói đã có gợi ý thì mở thẳng bước
        // duyệt, gói mới toanh thì bắt đầu từ đầu. Bấm "Tải lại" sau đó không
        // được nhảy bước, phá mất chỗ coach đang đứng.
        if (!this.stepInitialized) {
          this.stepInitialized = true;
          this.currentStep = this.items.length ? 3 : 1;
        }
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không tải được danh sách nhắc nhở',
        });
      } finally {
        this.loading = false;
      }
    },

    startGenerateProgressPolling() {
      this.stopGenerateProgressPolling();
      this.generateProgressTimer = setInterval(async () => {
        try {
          const p = await reminderAiService.getGenerateProgress(this.userPackageId);
          if (p && p.running) this.generateProgress = p;
        } catch (e) {
          // Lỗi đọc tiến độ không chặn luồng tạo, giữ số cũ
        }
      }, 1500);
    },

    stopGenerateProgressPolling() {
      if (this.generateProgressTimer) clearInterval(this.generateProgressTimer);
      this.generateProgressTimer = null;
      this.generateProgress = null;
    },

    // Vòng hỏi tiến độ DUY NHẤT: vừa cập nhật thanh tiến độ vừa chờ xong, trả về tiến độ cuối kèm lastResult
    async waitOtherGenerateDone() {
      // Lượt dài vài phút: một lần đọc tiến độ lỗi (mạng chập chờn) không có nghĩa BE đã dừng
      let errors = 0;
      while (!this._isDestroyed) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        try {
          const p = await reminderAiService.getGenerateProgress(this.userPackageId);
          errors = 0;
          if (!p || !p.running) return p || null;
          this.generateProgress = p;
        } catch (e) {
          if (++errors >= 5) return null;
        }
      }
      return null;
    },

    async handleGenerate() {
      this.generating = true;
      this.generateMessage = '';
      this.generateFailed = false;
      this.generateProgress = null;

      try {
        let result = await reminderAiService.generate({
          userPackageId: this.userPackageId,
          userInfoOverride:
            this.includesManualInfo ? this.userInfoOverride : null,
          ...this.generateSourcePayload,
          titleOverride: this.titleValue,
        });

        // BE chạy nền (lượt của mình hoặc lượt khác đang chạy): chờ xong rồi lấy kết quả lượt đó
        if (result?.inProgress) {
          this.generateMessage = result.message;
          const done = await this.waitOtherGenerateDone();
          result = done?.lastResult || null;
        }

        // Lần gợi ý đầu tiên dựng luôn cây hoạt động dưới DB, nạp lại để các nút
        // sửa / xoá / thêm được mở ra
        await Promise.all([this.loadData(), this.loadContext()]);

        // Không sinh được cái nào là tín hiệu cần xem lại prompt của nhóm, không phải thành công.
        // Mất kết quả (key hết hạn) thì dựa vào nháp vừa nạp
        this.generateFailed = result ? !result.generated : !this.step2Complete;
        this.generateMessage = result?.message
          || (this.generateFailed ? 'Tạo gợi ý thất bại' : 'Đã tạo gợi ý xong.');

        // Hỏng sạch (gọi AI lỗi hết) thì báo đỏ như trước, khi BE còn trả 500
        if (result && !result.generated && result.failed > 0) {
          this.$toastr.e({ title: 'Lỗi !', msg: this.generateMessage });
        }

        // Sinh được ít nhất một gợi ý thì mở luôn màn duyệt, đỡ phải tự bấm qua.
        // Kèm step2Complete: BE báo xong mà danh sách vẫn rỗng thì ở lại bước 2.
        if (!this.generateFailed && this.step2Complete) this.currentStep = 3;
      } catch (error) {
        // Proxy cắt kết nối khi gọi AI lâu, nhưng BE vẫn chạy tiếp và lưu nháp: chờ rồi nạp lại
        if (await this.recoverLostGenerate(error)) return;

        this.generateFailed = true;
        this.generateMessage = error.message || 'Tạo gợi ý thất bại';
        this.$toastr.e({ title: 'Lỗi !', msg: this.generateMessage });
      } finally {
        this.stopGenerateProgressPolling();
        this.generating = false;
      }
    },

    // Mất response (không phải lỗi nghiệp vụ từ BE) thì xem tiến độ thay vì báo lỗi ngay
    async recoverLostGenerate(error) {
      const lostResponse = !error || error.code === 'unknow' || error.code === 'timeout';
      if (!lostResponse) return false;

      try {
        this.generateMessage = 'Kết nối bị ngắt, đang chờ AI tạo xong...';
        await this.waitOtherGenerateDone();
        await Promise.all([this.loadData(), this.loadContext()]);
      } catch (e) {
        return false;
      }

      if (!this.step2Complete) return false;

      this.generateMessage = 'Đã tạo gợi ý xong.';
      this.generateFailed = false;
      this.currentStep = 3;
      return true;
    },

    async handleReview(item, action) {
      this.busy = true;
      try {
        await reminderAiService.review({
          id: item.id,
          action,
          // BE cần cái này để mở đúng nháp khi mục còn nằm trên Redis
          userPackageId: this.userPackageId,
        });

        this.$toastr.s({ title: 'Thành công !', msg: `Đã cập nhật ${item.code}` });
        await this.loadData();
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Cập nhật thất bại' });
      } finally {
        this.busy = false;
      }
    },

    // Bật/tắt ô sửa nội dung gợi ý AI ngay tại bảng duyệt. Đóng lại thì khoá về bản
    // đang hiển thị (finalContent), không âm thầm giữ bản gõ dở.
    toggleContentEdit(item) {
      const idx = this.editingContentIds.indexOf(item.id);
      if (idx === -1) {
        this.editingContentIds.push(item.id);
        this.$set(this.contentEditsById, item.id, this.toTagText(item.finalContent));
        return;
      }

      this.editingContentIds.splice(idx, 1);
      this.$delete(this.contentEditsById, item.id);
    },

    // CHỈ lưu nội dung, không duyệt: việc duyệt (và đặt job) dồn hết về nút "Tạo nhắc
    // nhở" ở chế độ duyệt từng tin, hoặc nút Duyệt từng dòng ở chế độ duyệt trước.
    async handleSaveContentEdit(item) {
      const editedContent = (this.contentEditsById[item.id] || '').trim();
      if (!editedContent) return;

      this.busy = true;
      try {
        await reminderAiService.saveContent({
          id: item.id,
          editedContent: this.toTagText(editedContent),
          userPackageId: this.userPackageId,
        });

        this.toggleContentEdit(item);
        this.$toastr.s({ title: 'Thành công !', msg: `Đã lưu nội dung ${item.code}` });
        await this.loadData();
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Lưu thất bại' });
      } finally {
        this.busy = false;
      }
    },

    async handleDiscardDraft() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Xoá toàn bộ gợi ý chưa duyệt để tạo lại từ đầu? '
          + 'Bản coach đã duyệt hoặc đã bỏ qua vẫn giữ nguyên. '
          + 'Tạo lại sẽ tốn thêm một lượt gọi AI.',
        {
          title: 'Xác nhận',
          okTitle: 'Xoá nháp',
          okVariant: 'danger',
          cancelTitle: 'Huỷ',
          cancelVariant: 'light',
        },
      );
      if (!confirmed) return;

      this.busy = true;
      try {
        await reminderAiService.discardDraft(this.userPackageId);
        this.generateMessage = '';
        this.$toastr.s({ title: 'Thành công !', msg: 'Đã xoá nháp' });
        await this.loadData();
        // Nháp mất rồi thì việc tiếp theo là gọi AI lại, không phải ngồi nhìn bảng trống
        this.currentStep = 2;
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Xoá nháp thất bại' });
      } finally {
        this.busy = false;
      }
    },

    async handleApproveAll() {
      // Bỏ dòng đã quá giờ: duyệt là đặt lịch, mà Hangfire từ chối lịch quá khứ
      const waiting = this.items.filter((i) => i.status === APPROVAL_STATUS.PENDING);
      const pending = waiting.filter((i) => !this.isPastDue(i));

      if (!pending.length) {
        // Nói rõ con số thay vì một câu chung: không có mục nào chờ duyệt, hay có mà
        // bị coi là quá giờ — hai nguyên nhân khác hẳn nhau nhưng trước đây cùng ra
        // một câu, nên không lần nào biết được đang vướng cái nào.
        return this.$toastr.e({
          title: 'Không duyệt được',
          msg: `Tổng ${this.items.length} mục, ${waiting.length} đang chờ duyệt, `
            + `${waiting.length - pending.length} đã quá giờ gửi. `
            + (waiting.length
              ? 'Đổi giờ gửi sang tương lai rồi thử lại.'
              : 'Không có mục nào ở trạng thái chờ duyệt.'),
        });
      }

      // Duyệt trước nội dung: đây là chốt cuối, gửi thẳng không hỏi lại.
      // Duyệt từng tin: đây chỉ là tạo nhắc nhở (đặt job Hangfire) — nội dung vẫn còn
      // một lượt hỏi coach qua Zalo ĐÚNG lúc tới giờ gửi, không phải báo trước
      // (xem TriggerFinalConfirm — hỏi tại T-0, chờ tối đa 30 phút coach trả lời).
      const customerName = this.context.customerName || 'khách hàng này';
      const confirmMsg = this.showPerItemReview
        ? `Duyệt nguyên bản ${pending.length} nhắc nhở đang chờ?`
        : `Xác nhận tạo ${pending.length} nhắc nhở cho ${customerName}? Trước giờ gửi 30 `
          + 'phút, coach sẽ nhận tin qua Zalo để xác nhận lần cuối. Chỉ nhắc nhở nào coach '
          + 'đồng ý mới được đặt lịch gửi; không trả lời trong 30 phút kể từ giờ gửi thì huỷ.';

      const confirmed = await this.$bvModal.msgBoxConfirm(confirmMsg, {
        title: 'Xác nhận',
        okTitle: this.showPerItemReview ? 'Duyệt hết' : 'Tạo nhắc nhở',
        okVariant: 'success',
        cancelVariant: 'light',
        cancelTitle: 'Huỷ',
      });
      if (!confirmed) return;

      this.busy = true;
      this.approveProgress = { done: 0, total: pending.length };
      let ok = 0;
      let failed = 0;
      // Giữ lỗi ĐẦU TIÊN để báo ra: chỉ nói "lỗi 3" thì coach không biết phải sửa gì
      let firstError = '';

      try {
        // Tuần tự chứ không Promise.all: duyệt hàng loạt mà nửa chừng lỗi thì
        // vẫn biết chính xác cái nào đã qua — và cho ra tiến độ thật để hiển thị
        for (const item of pending) {
          try {
            await reminderAiService.review({
              id: item.id,
              action: REVIEW_ACTION.OK,
              userPackageId: this.userPackageId,
            });
            ok += 1;
          } catch (error) {
            failed += 1;
            if (!firstError) firstError = error.message || '';
          }

          this.approveProgress = { done: ok + failed, total: pending.length };
        }

        const verb = this.showPerItemReview ? 'Duyệt' : 'Tạo';

        // Còn dòng lỗi thì Ở LẠI bước 3 để coach xử nốt, không đóng màn cuốn đi mất
        if (failed) {
          this.$toastr.e({
            title: 'Có lỗi !',
            msg: `${verb} được ${ok}, lỗi ${failed}.`
              + (firstError ? ` Lý do: ${firstError}` : ''),
          });

          await this.loadData();
          return;
        }

        this.$toastr.s({
          title: 'Hoàn tất !',
          msg: `Đã ${verb.toLowerCase()} xong ${ok} nhắc nhở.`,
        });

        // Hết việc ở màn AI — đóng luôn thay vì để coach đứng trước một bảng không
        // còn thao tác nào. Cha nghe @close rồi tự dọn và rời màn.
        this.$emit('close');
      } finally {
        // Giữ thanh tiến độ tới sau khi nạp lại bảng, khỏi nháy một khoảng trắng ở giữa
        this.busy = false;
        this.approveProgress = null;
      }
    },

    openChangeTime(item) {
      this.changeTimeItem = item;
      this.newSendAt = moment.unix(item.scheduledSendAt).format('YYYY-MM-DDTHH:mm');
      this.showChangeTime = true;
    },

    async handleChangeTime() {
      if (!this.changeTimeItem?.tranUserPackageActivityReminderId) {
        return this.$toastr.e({
          title: 'Lỗi !',
          msg: 'Nhắc nhở này chưa gắn với lịch gửi nào',
        });
      }

      this.busy = true;
      try {
        const result = await reminderAiService.changeTime({
          tranUserPackageActivityReminderId:
            this.changeTimeItem.tranUserPackageActivityReminderId,
          // Gửi lên cũng phải là Unix giây, đúng quy ước của hệ thống
          newSendAt: moment(this.newSendAt).unix(),
        });

        this.showChangeTime = false;
        this.$toastr.s({
          title: 'Thành công !',
          msg: result?.message || 'Đã đổi giờ gửi',
        });
        await this.loadData();
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message || 'Đổi giờ thất bại' });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.past-box {
  border: 1px solid #f64e60;
  border-left-width: 3px;
  border-radius: 4px;
  background: #fff5f6;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.past-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #c22b3c;
}
.past-note {
  color: #7e8299;
  font-size: 12.5px;
  margin: 4px 0 0;
}

.planned-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}
.planned-footer-actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}

.src-img-wrap {
  display: block;
  margin-top: 8px;
}
.src-img {
  display: block;
  max-width: 220px;
  max-height: 140px;
  object-fit: contain;
  border: 1px solid #ebedf3;
  border-radius: 4px;
  background: #f8f9fc;
}

.topic-cell { white-space: normal; }
.topic-tag {
  display: inline-block;
  background: #f3f6f9;
  color: #5e6278;
  border-radius: 4px;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 1.6;
}

.period-box {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 16px;
  background: #f9fafc;
}
.period-sub {
  margin-top: 2px;
  color: #7e8299;
  font-size: 12.5px;
}

.confirm-row {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #ebedf3;
}
.confirm-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  color: #3f4254;
}
.confirm-hint {
  margin-top: 4px;
  color: #7e8299;
  font-size: 12.5px;
}

.period-confirm {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #ffd07b;
  border-radius: 4px;
  background: #fff8ec;
}
.period-confirm-text {
  flex: 1;
  min-width: 240px;
  color: #8a6d3b;
  font-size: 12.5px;
}

.period-done {
  margin-top: 14px;
  color: #3f4254;
  font-size: 12.5px;
}

/* Thay cho hàng nút khi chưa chốt khung. Nói ra việc cần làm chứ không chỉ disable
   nút — nút xám không giải thích được vì sao nó xám. */
.period-gate {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #f3f6f9;
  color: #7e8299;
  font-size: 12.5px;
}

.period-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.range-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
}
.range-row .tsel { flex: 0 0 auto; min-width: 104px; }
.range-row.off { opacity: 0.5; }

.period-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.period-grid.off {
  opacity: 0.5;
}
.period-card {
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@media (max-width: 767px) {
  .period-split .period-grid {
    margin-top: 12px;
  }
}
.period-card.on {
  border-color: #3699ff;
  box-shadow: inset 0 0 0 1px #3699ff;
}
.period-card.bad {
  border-color: #f64e60;
  box-shadow: inset 0 0 0 1px #f64e60;
}
.period-bad {
  color: #f64e60;
  font-size: 11.5px;
  line-height: 1.45;
}
.period-time {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tsel,
.nsel {
  border: 1px solid #e4e6ef;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 13px;
  background-color: #fff;
  color: #3f4254;
}
.tsel { flex: 1; min-width: 0; height: auto; padding-right: 24px; } /* chừa chỗ mũi tên custom-select */
.tsel:disabled { background-color: #f3f6f9; color: #b5b5c3; }
.nsel { width: 66px; }
.tdash { color: #b5b5c3; }

.period-ok {
  color: #1bc5bd;
  font-size: 13px;
}
/* Nền đặc chứ không phải nút viền suông: đây là hành động xoá, phải nhìn ra ngay
   giữa hai nút xanh. Nền nhạt + chữ đậm, hai màu đủ cách nhau để còn đọc được. */
.aff-apply .btn-skip {
  background: #ffe2e5;
  border: 1px solid #f1a7b0;
  color: #c2153c;
  font-weight: 500;
}
.aff-apply .btn-skip:hover:not(:disabled) {
  background: #f64e60;
  border-color: #f64e60;
  color: #fff;
}
.aff-apply .btn-skip:disabled {
  opacity: 0.55;
}

.period-warn-note {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: #7e8299;
}

.period-warn {
  background: #fff4de;
  color: #a06a00;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 12px;
}

.aff-list {
  margin-top: 8px;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #ebedf3;
  border-radius: 4px;
  background: #fff;
}
.aff-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-bottom: 1px solid #f3f6f9;
  font-size: 12.5px;
}
.aff-row:last-child { border-bottom: none; }
.aff-when {
  flex: 0 0 96px;
  color: #7e8299;
  font-variant-numeric: tabular-nums;
}
.aff-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aff-topic {
  flex: 0 0 140px;
  color: #7e8299;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aff-to {
  flex: 0 0 auto;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.aff-to::before { content: '→ '; color: #b5b5c3; font-weight: 400; }
.aff-to.is-move { color: #1bc5bd; }
.aff-to.is-drop { color: #f64e60; }
.aff-to.is-manual { color: #7e8299; font-weight: 400; }

@media (max-width: 760px) {
  .aff-topic { display: none; }
}

.aff-apply {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

// Font/màu riêng cho tab AI Mode theo mockup coach cung cấp — ấm hơn tông xám lạnh
// mặc định của theme. Chỉ áp dụng trong .reminder-ai-tab, không đụng theme chung.
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700&family=Inter:wght@400;500;600&display=swap');

$ai-ink: #16231f;
$ai-ink-soft: #4b564f;
$ai-ink-faint: #8a9089;
$ai-page: #f3f3ee;
$ai-card: #ffffff;
$ai-line: #e1e0d6;
$ai-line-soft: #ebeae1;
$ai-teal: #0f6e56;
$ai-teal-dark: #0a4e3d;
$ai-teal-fill: #e7f2ee;
// Riêng nút hành động chính của mỗi bước: cố ý lệch khỏi hệ teal để không lẫn với
// các nút phụ (xem trước, điều hướng) vốn cũng đang teal.
$ai-action: #3699ff;
$ai-action-dark: #187de4;
$ai-action-line: #cfe5ff;
$ai-action-fill: #eaf4ff;
$ai-action-fill-hover: #d8ecff;
$ai-teal-line: #c5ded4;
$ai-teal-fill-hover: #d9eae4;

.reminder-ai-tab {
  background: $ai-page;
  font-family: 'Inter', sans-serif;
  color: $ai-ink;

  h1,
  h2,
  h3 {
    font-family: 'Manrope', sans-serif;
  }
}

// ---- Header thay cho title mặc định của b-modal (xem UserPackageUpdate.vue: hide-header) ----
.ai-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;

  &__text h1 {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 4px;
    letter-spacing: -0.01em;
    color: $ai-ink;
  }

  &__text p {
    margin: 0;
    font-size: 13px;
    color: $ai-ink-faint;
  }

  &__close {
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: $ai-ink-faint;
    font-size: 11px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:hover {
      background: $ai-line-soft;
    }
  }
}

// ---- Thanh bước: chấm số + connector, check khi đã xong, viền teal khi đang đứng ----
.ai-stepper {
  display: flex;
  align-items: flex-start;
  padding: 0 4px;

  &__item {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;

    &.is-locked {
      cursor: not-allowed;
    }
  }

  &__connector {
    position: absolute;
    top: 13px; // canh giữa chấm 26px
    left: calc(-50% + 13px);
    right: calc(50% + 13px);
    height: 2px;
    background: $ai-line;
    z-index: 0;

    &--done {
      background: $ai-teal;
    }
  }

  &__dot {
    position: relative;
    z-index: 1;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Manrope', sans-serif;
    border: 2px solid $ai-line;
    background: $ai-card;
    color: $ai-ink-faint;
    flex: none;

    i {
      font-size: 11px;
    }
  }

  &__item.is-done &__dot {
    background: $ai-teal;
    border-color: $ai-teal;
    color: #fff;
  }

  &__item.is-current &__dot {
    border-color: $ai-teal;
    color: $ai-teal;
    background: $ai-teal-fill;
  }

  &__label {
    margin-top: 8px;
    font-size: 12.5px;
    font-weight: 600;
    color: $ai-ink-faint;
    text-align: center;
  }

  &__item.is-done &__label,
  &__item.is-current &__label {
    color: $ai-ink;
  }
}

// ---- Khung nội dung từng bước, thay cho card.card-custom ----
.panel {
  background: $ai-card;
  border: 1px solid $ai-line;
  border-radius: 14px;
  padding: 28px 30px 24px;
}

.panel-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;

  .step-tag {
    flex: none;
    font-size: 12px;
    font-weight: 700;
    color: $ai-teal-dark;
    background: $ai-teal-fill;
    padding: 2px 8px;
    border-radius: 6px;
    font-family: 'Manrope', sans-serif;
  }

  h2 {
    font-size: 17px;
    font-weight: 700;
    margin: 0;
    color: $ai-ink;
  }
}

.panel > .desc {
  font-size: 13.5px;
  color: $ai-ink-soft;
  line-height: 1.6;
  margin: 10px 0 22px;
  max-width: 62ch;
}

// Câu dẫn của bước Gọi AI nói rõ AI sắp xử lý bao nhiêu hoạt động — là thông tin cần
// đọc chứ không phải chữ phụ, nên dựng thành khối có nền và căn giữa cùng trục với
// dropdown bên dưới. Phải đi kèm `.panel >` để thắng specificity của rule ở trên.
.panel > .desc--hero {
  max-width: 640px;
  margin: 14px auto 24px;
  padding: 14px 20px;
  text-align: center;
  font-size: 14px;
  color: $ai-teal-dark;
  background: $ai-teal-fill;
  border: 1px solid $ai-teal-line;
  border-radius: 10px;

  strong {
    font-weight: 700;
    color: $ai-ink;
  }
}

// ---- Hàng nút hành động (bước Gọi AI) ----
.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

// Hai việc xem trước là hai thứ khác nhau — danh sách hoạt động lấy từ chương trình, và
// prompt sắp gửi cho AI — nên tách nền hẳn ra, đừng để thành một cặp nút trông giống
// nhau rồi phải đọc chữ mới phân biệt được.
.btn--data {
  background: $ai-teal-fill;
  border-color: $ai-teal-line;
  color: $ai-teal-dark;

  &:hover:not(:disabled) {
    background: $ai-teal-fill-hover;
    border-color: $ai-teal;
  }
}

.btn--prompt {
  background: $ai-action-fill;
  border-color: $ai-action-line;
  color: $ai-action-dark;

  &:hover:not(:disabled) {
    background: $ai-action-fill-hover;
    border-color: $ai-action;
  }
}

.btn-primary {
  background: $ai-teal;
  border-color: $ai-teal;
  color: #fff;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: $ai-teal-dark;
    border-color: $ai-teal-dark;
  }
}

.divider {
  border: none;
  border-top: 1px solid $ai-line-soft;
  margin: 0 0 22px;
}

.footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: 500;
  color: $ai-ink-soft;
  background: $ai-line-soft;
  border: none;
  padding: 9px 14px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: $ai-line;
  }
}

.footer-action {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.approve-progress {
  margin-bottom: 18px;

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: $ai-ink-soft;
    margin-bottom: 6px;
  }
}

.btn-view {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: $ai-ink-faint;
  background: $ai-card;
  border: 1px solid $ai-line;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:not(:disabled) {
    color: $ai-teal-dark;
    border-color: $ai-teal;
  }

  .count {
    background: $ai-line-soft;
    color: $ai-ink-soft;
    font-size: 11.5px;
    padding: 1px 7px;
    border-radius: 20px;
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
  }
}

// Cùng một vị trí nút, nhưng khi nó là việc phải làm (chưa có gợi ý -> gọi AI) thì tô
// đậm để đọc ra ngay; lúc chỉ còn là nút điều hướng sang bước duyệt thì để nhạt.
.btn-view--primary {
  color: #fff;
  background: $ai-action;
  border-color: $ai-action;

  &:not(:disabled) {
    color: #fff;
    border-color: $ai-action;
  }

  &:hover:not(:disabled) {
    background: $ai-action-dark;
    border-color: $ai-action-dark;
  }
}

// ---- Reskin b-button/alert còn lại ở bước 1, 3 và các modal con — chỉ đổi giao
// diện cho khớp bảng màu mới, không đụng markup/logic của chúng. ----
::v-deep .btn {
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
}

::v-deep .btn-success {
  background-color: $ai-teal;
  border-color: $ai-teal;

  &:hover,
  &:focus,
  &:not(:disabled):not(.disabled):active {
    background-color: $ai-teal-dark;
    border-color: $ai-teal-dark;
  }
}

::v-deep .btn-outline-success {
  color: $ai-teal;
  border-color: $ai-teal;

  &:hover,
  &:focus {
    background-color: $ai-teal;
    border-color: $ai-teal;
  }
}

::v-deep .alert {
  border-radius: 8px;
}

// Danh sách chỉ số giờ chiếm cả chiều ngang, xếp cột cho khỏi thành một dải dài
.survey-group {
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;

  &:last-child {
    border-bottom: 0;
    margin-bottom: 0;
  }
}

.survey-analysis {
  padding: 12px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background-color: #f9f9fc;
}

.survey-section {
  padding-left: 12px;
  margin: 8px 0 12px;
  border-left: 2px solid #e4e6ef;
}

.survey-section__name {
  font-weight: 500;
  color: #5e6278;
}

.survey-answer {
  color: #3699ff;
}

.metric-tree {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}

.metric-node {
  border-bottom: 1px solid #ebedf3;

  &:last-child {
    border-bottom: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
  }

  &__toggle {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    background: none;
    border: none;
    padding: 0;
    text-align: left;
    cursor: pointer;
  }

  &__name {
    font-weight: 500;
    color: #3f4254;
    flex: none;
  }

  &__badge {
    font-size: 0.85rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    margin-left: auto;
    flex: none;
    font-style: normal;
    color: #7e8299;
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__detail {
    padding: 0 14px 14px 40px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.metric-window {
  font-size: 0.85rem;
  padding-left: 12px;
  border-left: 2px solid #ebedf3;

  &__row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__toggle {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    padding: 2px 0;
    text-align: left;
    cursor: pointer;
  }

  &__label {
    flex: none;
    width: 56px;
    color: #7e8299;
  }

  &__value {
    color: #3f4254;
  }

  &__chevron {
    margin-left: auto;
    flex: none;
    font-style: normal;
    color: #7e8299;
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__entries {
    padding: 2px 0 6px 66px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__entry {
    display: flex;
    gap: 10px;
    font-size: 0.8rem;

    span:first-child {
      color: #3f4254;
      min-width: 70px;
    }
  }
}

// Tag của multiselect không có margin dưới, mà .multiselect__tags lại
// padding-bottom: 0 — chọn 3 nguồn là chip xuống dòng và dính sát vào nhau.
::v-deep .multiselect__tags {
  padding-bottom: 0.25rem;
}

::v-deep .multiselect__tag {
  margin-bottom: 0.25rem;
}

.reminder-ai-tab {
  .prompt-preview {
    background: #f7f8fa;
    border: 1px solid #ebedf3;
    border-radius: 4px;
    padding: 1rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.82rem;
    font-family: inherit;
    color: #3f4254;
    margin: 0;
  }

  .ctx-head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .ctx-name {
    font-size: 1.15rem;
    font-weight: 600;
    color: #3f4254;
  }

  .ctx-title {
    color: #7e8299;
  }

  .source-cell,
  .content-cell {
    white-space: pre-wrap;
    word-break: break-word;
    min-width: 220px;
    max-width: 380px;
  }

  // Giờ gửi không được ngắt dòng giữa ngày và chữ "đã qua" — cột hẹp nên nếu để tự do
  // thì một mốc giờ bị xé thành ba dòng.
  .send-at {
    white-space: nowrap;
  }

  // Ba thao tác của một dòng nằm cùng một hàng icon, cách đều.
  // Cỡ icon đặt thẳng ở đây thay vì dùng class icon-sm/icon-lg của theme: thang đo đó
  // nằm trong CSS đã biên dịch của Metronic, sửa ở đây thì chắc chắn hơn.
  .row-actions {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 8px;

    i {
      font-size: 1.25rem;
    }

    a {
      color: $ai-action;
      line-height: 1;

      &:hover {
        color: $ai-action-dark;
      }

      // Gộp vào trong `a` để thắng specificity của `.row-actions a` ở trên,
      // khỏi phải dùng !important
      &.row-actions__danger {
        color: #f64e60;

        &:hover {
          color: #ee2d41;
        }
      }
    }
  }

  .source-cell {
    font-size: 0.9rem;
  }

  table td {
    vertical-align: top;
  }
}
</style>
